import React, { useState, useEffect, useCallback, useRef, Suspense, lazy } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { Typography, Box, CircularProgress, RadioGroup, FormControlLabel, Radio, LinearProgress, Stepper, Step, StepLabel, StepConnector, withStyles } from '@material-ui/core';
import QuestionRenderer from './QuestionRenderer';
import EnhancedQuestionRenderer from './EnhancedQuestionRenderer';
import './EnhancedQuestionRenderer.css';
import firebase from "firebase/app";
import "firebase/database";
import "firebase/storage";
import swal from 'sweetalert';
import AIEvaluationService from '../services/AIEvaluationService';
import VideoAnalysisService from '../services/VideoAnalysisService';
import { AssignmentTurnedIn, ListAlt, Code, HelpOutline, FormatListBulleted } from '@material-ui/icons';
import './DynamicExamScreen.css';
import './SecurityAlerts.css';
import Button from 'react-bootstrap/Button';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import { ArrowLeft, ArrowRight, CheckCircle, FlagFill } from 'react-bootstrap-icons';
import 'bootstrap/dist/css/bootstrap.min.css';
import logo from '../rcrm_logo.png';
import ExamSecurity from './ExamSecurity';
import DetectRTC from 'detectrtc';
import SubmissionProgressModal from './SubmissionProgressModal';
import ShortUrlService from '../services/ShortUrlService';
import AuthenticatedUrlCache from '../services/AuthenticatedUrlCache';
import ExamCacheService from '../services/ExamCacheService';
import CheatDetectionConfig from '../services/CheatDetectionConfig';


  // Function to get display URL for images (handles both legacy and new)
  const getDisplayUrl = async (imageUrl, questionId = null, retryCount = 0) => {
    if (!imageUrl) {
      return '';
    }
    
    const MAX_RETRIES = 2;
    
    // If it's already a full URL (Firebase Storage, HTTP, data URI), return as-is
    if (imageUrl.includes('firebase') || imageUrl.startsWith('http') || imageUrl.startsWith('data:') || imageUrl.startsWith('blob:')) {
      return imageUrl;
    }
    
    // Try ShortUrlService first if available (handles short URLs properly)
    try {
      if (ShortUrlService && ShortUrlService.PROJECT_ID) {
        const fullUrl = await ShortUrlService.toFullUrl(imageUrl);
        if (fullUrl && fullUrl !== imageUrl && (fullUrl.includes('firebase') || fullUrl.startsWith('http'))) {
          console.log(`✅ Image loaded via ShortUrlService: ${imageUrl}`);
          return fullUrl;
        }
      }
    } catch (shortUrlError) {
      console.warn(`⚠️ ShortUrlService failed, trying direct paths:`, shortUrlError.message);
      // Continue to manual path resolution
    }
    
    let finalUrl;
    const pathsToTry = [];
    
    // Build potential paths based on different storage formats
    if (imageUrl.startsWith('q-')) {
      // New format with q- prefix: q-{questionId}/{filename}
      pathsToTry.push(`image-questions/${imageUrl}`);
    } else if (imageUrl.includes('/')) {
      // Contains a slash, might be: {questionId}/{filename} or q-{questionId}/{filename}
      if (imageUrl.startsWith('img_')) {
        pathsToTry.push(`image-questions/${imageUrl}`);
      } else {
        // Try with q- prefix first (new format)
        const parts = imageUrl.split('/');
        if (parts.length === 2) {
          pathsToTry.push(`image-questions/q-${parts[0]}/${parts[1]}`);
          pathsToTry.push(`image-questions/${imageUrl}`);
        } else {
          pathsToTry.push(`image-questions/${imageUrl}`);
        }
      }
    } else if (questionId) {
      // Short filename, try with questionId (new format)
      pathsToTry.push(`image-questions/q-${questionId}/${imageUrl}`);
      pathsToTry.push(`image-questions/${questionId}/${imageUrl}`);
      pathsToTry.push(`image-questions/${imageUrl}`);
    } else {
      // Fallback: try direct path
      pathsToTry.push(`image-questions/${imageUrl}`);
    }
    
    // Try each path until one succeeds
    for (const path of pathsToTry) {
      try {
        const storageRef = firebase.storage().ref(path);
        finalUrl = await storageRef.getDownloadURL();
        console.log(`✅ Image loaded successfully: ${path}`);
        return finalUrl; // Success - return immediately
      } catch (error) {
        console.warn(`⚠️ Failed to load image from path: ${path}`, error.message);
        // Continue to next path
      }
    }
    
    // If all paths failed and we have retries left, retry after a delay
    if (retryCount < MAX_RETRIES) {
      console.log(`🔄 Retrying image load (attempt ${retryCount + 1}/${MAX_RETRIES})...`);
      await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1))); // Exponential backoff
      return getDisplayUrl(imageUrl, questionId, retryCount + 1);
    }
    
    // All paths failed, return original (will show error in component)
    console.error(`❌ Failed to load image after all attempts: ${imageUrl}`);
    return imageUrl;
  };

// Authenticated Image Component with Lazy Loading and Retry Logic
const AuthenticatedImage = ({ imageUrl, alt, style, questionId }) => {
  const [src, setSrc] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isVisible, setIsVisible] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const imgRef = useRef();
  const loadingAttemptRef = useRef(false); // Prevent concurrent loads

  useEffect(() => {
    // Intersection Observer for lazy loading with better thresholds
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { 
        threshold: 0.01, // Trigger earlier (1% visibility)
        rootMargin: '100px' // Start loading 100px before image enters viewport
      }
    );

    const currentRef = imgRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []); // Empty deps - observer should only be set up once

  // Also trigger loading if image becomes visible immediately (for images already in viewport)
  useEffect(() => {
    if (!imageUrl || isVisible || loadingAttemptRef.current) return;
    
    // Small delay to ensure ref is set
    const checkVisibility = () => {
      if (!imgRef.current) {
        // Retry after a short delay
        setTimeout(checkVisibility, 100);
        return;
      }
      
      // Check if element is already visible (no need to wait for Intersection Observer)
      const rect = imgRef.current.getBoundingClientRect();
      const isInViewport = rect.top < window.innerHeight + 200 && rect.bottom > -200;
      
      if (isInViewport) {
        setIsVisible(true);
      }
    };
    
    checkVisibility();
  }, [imageUrl, isVisible]);

  useEffect(() => {
    const loadImage = async () => {
      if (!isVisible || !imageUrl || loadingAttemptRef.current) return; // Prevent concurrent loads
      
      loadingAttemptRef.current = true;
      
      try {
        setLoading(true);
        setError(false);
        setErrorMessage('');
        
        console.log(`🖼️ Loading image for question ${questionId}:`, imageUrl);
        
        const authenticatedUrl = await getDisplayUrl(imageUrl, questionId);
        
        // Validate the URL before setting it
        if (!authenticatedUrl || authenticatedUrl === imageUrl) {
          // Check if it's a valid full URL or if we need to retry
          if (!authenticatedUrl.includes('firebase') && !authenticatedUrl.startsWith('http') && !authenticatedUrl.startsWith('data:')) {
            throw new Error('Failed to get valid image URL');
          }
        }
        
        setSrc(authenticatedUrl);
        console.log(`✅ Image URL resolved for question ${questionId}`);
      } catch (err) {
        console.error(`❌ Error loading image for question ${questionId}:`, err);
        setError(true);
        setErrorMessage(err.message || 'Failed to load image');
        
        // Auto-retry after delay if retries remaining
        if (retryCount < 2) {
          setTimeout(() => {
            setRetryCount(prev => prev + 1);
            loadingAttemptRef.current = false; // Allow retry
          }, 2000 * (retryCount + 1)); // Exponential backoff: 2s, 4s
          return; // Don't set loading to false yet
        }
      } finally {
        if (retryCount === 0 || !error) {
          // Only stop loading if it's the first attempt or if not in retry mode
          setLoading(false);
        }
        loadingAttemptRef.current = false;
      }
    };

    if (imageUrl && isVisible) {
      loadImage();
    }
  }, [imageUrl, questionId, isVisible, retryCount]);

  // Reset retry count when imageUrl changes
  useEffect(() => {
    setRetryCount(0);
    setError(false);
    setSrc('');
    setLoading(true);
    loadingAttemptRef.current = false;
  }, [imageUrl]);

  if (!imageUrl) {
    return null; // Don't render anything if no imageUrl
  }

  if (loading) {
    return (
      <div 
        ref={imgRef} 
        style={{ 
          ...style, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          background: '#f5f5f5',
          minHeight: style?.minHeight || '300px',
          border: '1px solid #e0e0e0',
          borderRadius: '8px'
        }}
      >
        <div style={{ textAlign: 'center', color: '#666' }}>
          <CircularProgress size={24} style={{ marginBottom: '8px' }} />
          <div style={{ fontSize: '12px' }}>Loading image...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div 
        ref={imgRef}
        style={{ 
          ...style, 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center', 
          justifyContent: 'center', 
          background: '#ffebee', 
          color: '#d32f2f', 
          fontSize: '12px', 
          textAlign: 'center', 
          padding: '16px',
          minHeight: style?.minHeight || '300px',
          border: '1px solid #ffcdd2',
          borderRadius: '8px'
        }}
      >
        <div style={{ fontSize: '24px', marginBottom: '8px' }}>📁</div>
        <div style={{ fontWeight: 600, marginBottom: '4px' }}>Image not found</div>
        <div style={{ fontSize: '11px', color: '#b71c1c' }}>
          {errorMessage || 'File may have been deleted or never uploaded'}
        </div>
        {retryCount < 2 && (
          <div style={{ marginTop: '8px', fontSize: '11px', color: '#666' }}>
            Retrying... ({retryCount + 1}/3)
          </div>
        )}
      </div>
    );
  }

  return (
    <img 
      ref={imgRef}
      src={src} 
      alt={alt} 
      style={{
        ...style,
        objectFit: 'contain',
        width: '100%',
        height: 'auto',
        display: 'block',
        transition: 'opacity 0.3s ease'
      }}
      onError={(e) => {
        console.error(`❌ Image onError triggered for question ${questionId}:`, src);
        setError(true);
        setErrorMessage('Failed to display image');
        
        // Retry loading with delay
        if (retryCount < 2) {
          setTimeout(() => {
            setRetryCount(prev => prev + 1);
            setSrc(''); // Clear src to trigger reload
            setLoading(true);
          }, 2000);
        }
      }}
      onLoad={() => {
        console.log(`✅ Image loaded successfully for question ${questionId}`);
        setLoading(false);
        setError(false);
      }}
    />
  );
};

const Detection = lazy(() => import('./Detections'));

// Custom Step Icon for progressive stepper
function CustomStepIcon(props) {
    const { active, completed, icon } = props;
    // All steps: show checkmark if completed, otherwise show number
    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 28,
                height: 28,
                borderRadius: '50%',
                background: completed ? '#1976d2' : '#fff',
                border: active ? '3px solid #1976d2' : '2px solid #cfd8dc',
                color: completed ? '#fff' : '#1976d2',
                fontWeight: 700,
                fontSize: 16,
                boxShadow: active ? '0 2px 8px rgba(25,118,210,0.13)' : 'none',
                transition: 'all 0.3s',
            }}
        >
            {completed ? <span style={{fontSize:18}}>✓</span> : icon}
        </div>
    );
}

// Custom connector for progressive fill with animation
const CustomConnector = withStyles({
    alternativeLabel: {
        top: 16,
    },
    active: {
        '& $line': {
            backgroundImage: 'linear-gradient(90deg, #1976d2 60%, #43a047 100%)',
            transition: 'background-image 0.6s cubic-bezier(.4,2,.6,1)',
        },
    },
    completed: {
        '& $line': {
            backgroundImage: 'linear-gradient(90deg, #1976d2 60%, #43a047 100%)',
            transition: 'background-image 0.6s cubic-bezier(.4,2,.6,1)',
        },
    },
    line: {
        height: 4,
        border: 0,
        backgroundColor: '#e0e0e0',
        borderRadius: 2,
        transition: 'background-image 0.6s cubic-bezier(.4,2,.6,1)',
    },
})(StepConnector);

// Section and question color palettes
const sectionColors = [
  '#1976d2', // blue
  '#43a047', // green
  '#ff9800', // orange
  '#8e24aa', // purple
  '#0097a7', // teal
  '#d32f2f', // red
  '#fbc02d', // yellow
  '#5d4037', // brown
];
const questionColors = [
  '#c62828', // deep red
  '#1565c0', // deep blue
  '#2e7d32', // deep green
  '#ad1457', // deep pink
  '#6d4c41', // deep brown
  '#f9a825', // deep yellow
  '#00838f', // deep teal
  '#6a1b9a', // deep purple
];

// Maximum number of security warnings (all violation types combined) a candidate
// may receive before the assessment is auto-submitted and closed.
const MAX_SECURITY_WARNINGS = 3;

// How long an auto-submission waits for pending video uploads before saving anyway
const VIDEO_SAVE_TIMEOUT_MS = 60000;

// Violations landing within this window of the last counted warning belong to the same
// action (e.g. Ctrl+C trips copy-paste and ctrl-key detection) and count only once
const WARNING_GROUP_WINDOW_MS = 2000;

// When one action trips several detectors it is still one warning, but the candidate should
// be shown the message that actually tells them what to fix. Opening a side panel also blurs
// the window, and "close the side panel" is far more useful than "you left the window".
const ALERT_PRIORITY = { sidePanel: 3, focusLoss: 2 };
const alertPriorityOf = (type) => ALERT_PRIORITY[type] || 1;

const DynamicExamScreen = () => {
    const { examCode } = useParams();
    const history = useHistory();
    const [exam, setExam] = useState(null);
    const [answers, setAnswers] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [loading, setLoading] = useState(true);
    const [examReady, setExamReady] = useState(false); // Track when exam is ready for timer
    const [timerStarted, setTimerStarted] = useState(false); // Track if timer has actually started
    const [submissionProgress, setSubmissionProgress] = useState({
        open: false,
        currentStep: 0,
        progress: 0,
        status: 'processing',
        errorMessage: ''
    });
    const user = JSON.parse(localStorage.getItem('user'));
    const userName = user?.name || sessionStorage.getItem("checkname") || 'Unknown Student';
    const [minutes, setMinutes] = useState(0);
    const [seconds, setSeconds] = useState(0);
    const [currentSection, setCurrentSection] = useState(0);
    const totalSections = exam?.sections.length || 0;
    const [stepperAnim, setStepperAnim] = useState('');
    const prevSectionRef = useRef(currentSection);
    const timerRef = useRef();
    const autoSubmitRef = useRef();
    const mountedRef = useRef(true); // Track if component is mounted
    const timerValuesRef = useRef({ minutes: 0, seconds: 0 }); // Track timer values in ref to avoid closure issues
    const timerLockRef = useRef(false); // Lock to prevent concurrent timer updates
    const [questionErrors, setQuestionErrors] = useState({});
    const [showLoader, setShowLoader] = useState(false);
    const [showDetection, setShowDetection] = useState(false);
    const securityRef = useRef(null);
    // Total security warnings issued so far (all violation types combined)
    const warningCountRef = useRef(parseInt(sessionStorage.getItem("count_totalWarnings") || "0"));
    // Set once the warning limit is hit, so no further alerts/redirects interrupt the final submission
    const examTerminatedRef = useRef(false);
    // When the last warning was counted, used to group one action's violations together
    const lastWarningAtRef = useRef(0);
    // Priority of the alert currently shown for that group, so a better message can replace it
    const lastAlertPriorityRef = useRef(0);
    // Message shown in the submission modal when the assessment is ended on the warning limit
    const [terminationNotice, setTerminationNotice] = useState('');
    const isLoadTesting = (typeof process !== 'undefined' && process && process.env && process.env.REACT_APP_LOAD_TESTING === '1');
    const [cheatCounts, setCheatCounts] = useState({
        alt: 0,
        ctrl: 0,
        tab: 0,
        face: 0,
        fullscreen: 0,
        copyPaste: 0,
        webcamOff: 0,
        contextMenu: 0,
        noPerson: 0,
        multiplePersons: 0,
        suspiciousObjects: 0
    });
    
    // Cheat detection settings from exam data
    const [cheatDetectionSettings, setCheatDetectionSettings] = useState(null);

    // Ref to store VideoRecorder component references
    const videoRecorderRefs = useRef({});

    // Function to save all unsaved videos during exam submission
    const saveAllVideosForSubmission = useCallback(async () => {
        const videoSavePromises = [];
        const videoQuestions = [];
        const videoAnswers = {};
        
        // Find all video questions and upload them to Firebase Storage
        // CRITICAL: Upload ALL videos that don't have a videoPath (Firebase Storage path)
        // This ensures blob URLs are never saved to Firebase - videos must be uploaded first
        exam?.sections.forEach(section => {
            section.questions.forEach(question => {
                if (question.type === 'video') {
                    const answer = answers[question.id];
                    
                    // Check if video needs to be uploaded (no videoPath = not uploaded yet)
                    const needsUpload = !answer || !answer.videoPath || 
                                      (answer.videoUrl && answer.videoUrl.startsWith('blob:'));
                    
                    if (needsUpload) {
                        const videoRecorderRef = videoRecorderRefs.current[question.id];
                        if (videoRecorderRef && videoRecorderRef.hasUnsavedVideo()) {
                            videoQuestions.push(question.id);
                            videoSavePromises.push(
                                videoRecorderRef.saveVideoForSubmission().then(videoAnswer => {
                                    if (videoAnswer && videoAnswer.videoPath) {
                                        videoAnswers[question.id] = videoAnswer;
                                    } else {
                                        throw new Error(`Video upload failed: No videoPath returned for question ${question.id}`);
                                    }
                                    return videoAnswer;
                                }).catch(error => {
                                    console.error('Video upload failed:', error);
                                    throw error;
                                })
                            );
                        }
                    }
                }
            });
        });

        if (videoSavePromises.length > 0) {
            console.log(`Saving ${videoSavePromises.length} videos for submission...`);
            console.log(`Video questions being uploaded: ${videoQuestions.join(', ')}`);
            try {
                const results = await Promise.all(videoSavePromises);
                console.log('All videos saved successfully for submission');
                console.log('Video answers collected:', videoAnswers);
                return videoAnswers; // Return the collected video answers
            } catch (error) {
                console.error('Error saving videos for submission:', error);
                
                // If it's a component unmounted error, we can still continue with submission
                // as the video might have been uploaded successfully
                if (error.message && error.message.includes('Component unmounted during upload')) {
                    console.warn('Component was unmounted during upload, but continuing with submission');
                    // Return any video answers that were collected before the error
                    return videoAnswers;
                }
                
                throw error;
            }
        } else {
            console.log('No unsaved videos found for submission');
            return {}; // Return empty object if no videos
        }
    }, [exam]);

    const sectionIcons = [<ListAlt />, <AssignmentTurnedIn />, <Code />, <HelpOutline />, <FormatListBulleted />];

            // Delay detection component rendering to prevent stuttering
        useEffect(() => {
            if (!isLoadTesting) {
                const timer = setTimeout(() => {
                    setShowDetection(true);
                }, 1000); // Reduced delay to 1 second for better UX
                return () => clearTimeout(timer);
            }
        }, [isLoadTesting]);

    // The security popup every detected violation raises, whichever detector found it
    const showSecurityAlert = useCallback((alertMessage) => {
        swal({
            title: "Security Alert",
            text: alertMessage,
            icon: "warning",
            button: "I Understand",
            className: "security-alert",
            closeOnClickOutside: false,
            closeOnEsc: false,
            timer: 5000,
            timerProgressBar: true
        });
    }, []);

    // End the assessment once the security warning limit is reached: lock it down, submit
    // whatever has been answered so far and move the candidate on to the thank-you page.
    const endExamOnWarningLimit = useCallback((alertMessage = '') => {
        if (examTerminatedRef.current) {
            return;
        }
        examTerminatedRef.current = true;
        // Persist it - a remount (e.g. coming back from the fullscreen alert page) must not resume the exam
        sessionStorage.setItem("exam_terminated", "true");
        console.log(`🚨 Warning limit of ${MAX_SECURITY_WARNINGS} reached - ending assessment`);

        // Stop further security alerts while the submission runs
        if (securityRef.current) {
            securityRef.current.pauseSecurityAlerts();
        }

        // Replace any open security alert with the submission progress modal, so the candidate
        // watches the submission run instead of staring at a dialog they cannot dismiss
        swal.close();
        setTerminationNotice(
            `${alertMessage} Your assessment has been closed due to repeated security violations.`.trim()
        );

        if (autoSubmitRef.current) {
            autoSubmitRef.current({
                terminated: true,
                reason: `Exceeded maximum security warnings (${MAX_SECURITY_WARNINGS})`
            });
        } else {
            // No submit handler yet (exam never finished loading) - don't leave them sitting in the exam
            console.error('🚨 No auto-submit handler available - redirecting without submitting');
            history.push('/thankyou');
        }
    }, [history]);

    // An assessment ended for security violations stays ended, even if this screen remounts
    useEffect(() => {
        // Warnings belong to a single assessment - drop anything carried over from another one.
        // Remounts within the same exam (e.g. the fullscreen alert page) keep their count.
        if (sessionStorage.getItem("warnings_exam_code") !== examCode) {
            sessionStorage.setItem("warnings_exam_code", examCode);
            sessionStorage.removeItem("count_totalWarnings");
            sessionStorage.removeItem("exam_terminated");
            warningCountRef.current = 0;
        }

        if (sessionStorage.getItem("exam_terminated") === "true" && !examTerminatedRef.current) {
            examTerminatedRef.current = true;
            console.log('🚨 Assessment was already ended for security violations - redirecting');
            history.push('/thankyou');
        }
    }, [examCode, history]);

    // Initialize security system when cheat detection settings are loaded
    const initializeSecuritySystem = useCallback(() => {
        if (!isLoadTesting && cheatDetectionSettings && !securityRef.current) {
            console.log('🔒 Initializing security system with settings:', cheatDetectionSettings);
            
            securityRef.current = new ExamSecurity();
            
            // Get security configuration based on cheat detection settings
            const securityConfig = CheatDetectionConfig.getSecurityConfig(cheatDetectionSettings);
            const alertMessages = CheatDetectionConfig.getAlertMessages(cheatDetectionSettings);
            
            console.log('🔒 Security config:', securityConfig);
            console.log('🔒 Alert messages:', alertMessages);
            
            securityRef.current.init({
                webcamRequired: securityConfig.webcamRequired,
                fullscreenRequired: securityConfig.fullscreenRequired,
                cheatDetectionSettings: cheatDetectionSettings,
                onCheatDetected: (type, count, message) => {
                    console.log(`🔍 Cheat detected: ${type}, count: ${count}, message: ${message}`);
                    
                    // Only process cheat detection if the feature is enabled
                    const featureKey = type === 'copyPaste' ? 'enableCopyPasteDetection' :
                        type === 'tabSwitch' ? 'enableTabSwitchDetection' :
                        type === 'fullscreen' ? 'enableFullscreenDetection' :
                        type === 'altKey' || type === 'ctrlKey' ? 'enableKeyboardDetection' :
                        type === 'contextMenu' ? 'enableContextMenuDetection' :
                        type === 'webcamOff' || type === 'noPerson' || type === 'multiplePersons' || type === 'suspiciousObjects' ? 'enableWebcamDetection' :
                        type === 'sidePanel' ? 'enableSidePanelDetection' :
                        type === 'focusLoss' ? 'enableFocusLossDetection' :
                        'enableTabSwitchDetection'; // default
                    
                    const isEnabled = CheatDetectionConfig.isFeatureEnabled(cheatDetectionSettings, featureKey);
                    console.log(`🔧 Feature ${featureKey} enabled: ${isEnabled}`);
                    
                    if (!isEnabled) {
                        console.log(`🔧 Cheat detection disabled for ${type} (${featureKey})`);
                        return;
                    }
                    
                    // Get enabled cheat count keys
                    const enabledKeys = CheatDetectionConfig.getCheatCountKeys(cheatDetectionSettings);
                    const sessionKey = enabledKeys[type] || `count_${type}`;
                    
                    console.log(`🔧 Session key for ${type}: ${sessionKey}`);
                    
                    if (sessionKey) {
                        const currentCount = parseInt(sessionStorage.getItem(sessionKey) || "0");
                        sessionStorage.setItem(sessionKey, (currentCount + 1).toString());
                        console.log(`🔧 Updated count for ${type}: ${currentCount + 1}`);
                    }
                    
                    // Get professional alert message
                    const alertMessage = alertMessages[type] || message;
                    console.log(`🔧 Alert message for ${type}: ${alertMessage}`);

                    // Assessment already ended - ignore any trailing violations
                    if (examTerminatedRef.current) {
                        console.log(`🔧 ${type} ignored - the assessment has already been ended`);
                        return;
                    }

                    // A single action can trip several detectors at once (Ctrl+C fires both
                    // copyPaste and ctrlKey; opening a side panel also blurs the window). Group
                    // those into one warning, otherwise one action burns several of them and the
                    // alerts replace each other before the candidate can read them.
                    const now = Date.now();
                    const priority = alertPriorityOf(type);
                    if (now - lastWarningAtRef.current < WARNING_GROUP_WINDOW_MS) {
                        // Same action, already counted - but if this violation explains the
                        // problem better than the alert on screen, show the better message
                        if (priority > lastAlertPriorityRef.current) {
                            console.log(`🔧 ${type} grouped with the previous warning - showing its alert instead`);
                            lastAlertPriorityRef.current = priority;
                            showSecurityAlert(alertMessage);
                        } else {
                            console.log(`🔧 ${type} grouped with the previous warning - not counted`);
                        }
                        return;
                    }
                    lastWarningAtRef.current = now;
                    lastAlertPriorityRef.current = priority;

                    // Count this violation towards the overall warning limit
                    warningCountRef.current += 1;
                    sessionStorage.setItem("count_totalWarnings", warningCountRef.current.toString());
                    console.log(`🚨 Security warning ${warningCountRef.current} of ${MAX_SECURITY_WARNINGS}`);

                    // Warning limit reached - submit whatever has been answered and end the assessment
                    if (warningCountRef.current >= MAX_SECURITY_WARNINGS) {
                        endExamOnWarningLimit(alertMessage);
                        return;
                    }

                    // Show alert to user
                    showSecurityAlert(alertMessage);
                },
                onFullscreenExit: () => {
                    // The warning limit was just hit - stay put so the auto-submission can finish
                    if (examTerminatedRef.current) {
                        return;
                    }
                    if (securityConfig.fullscreenRequired) {
                        history.push("/enhanced-fullscreen-alert");
                    }
                }
            });
            
            // Expose security system globally for VideoRecorder component
            window.examSecurity = securityRef.current;
            console.log('🔒 Security system initialized and exposed globally');
            
            // Add test function for debugging
            window.testCheatDetection = (type = 'tabSwitch') => {
                console.log(`🧪 Testing cheat detection for type: ${type}`);
                if (securityRef.current) {
                    securityRef.current.recordCheat(type, `Test ${type} detection`);
                }
            };
            console.log('🧪 Test function added: window.testCheatDetection(type)');
        }
    }, [isLoadTesting, cheatDetectionSettings, history, endExamOnWarningLimit, showSecurityAlert]);

    // Initialize security system when cheat detection settings change
    useEffect(() => {
        initializeSecuritySystem();
    }, [initializeSecuritySystem]);

    useEffect(() => {
        const loadExamData = async () => {
            try {
                // Check cache first for exam data
                const cachedExam = ExamCacheService.getExamCache(examCode);
                if (cachedExam) {
                    console.log('📖 Using cached exam data:', examCode);
                    
                    // Process cached exam data
                    if (cachedExam && (cachedExam.isDynamic || cachedExam.dynamic)) {
                        const normalizedExam = {
                            examCode: examCode,
                            examTitle: cachedExam.title || cachedExam.examTitle,
                            sections: cachedExam.sections ? cachedExam.sections.map(section => ({
                                id: section.id,
                                title: section.t || section.title,
                                weightage: section.w || section.weightage,
                                questions: section.q || section.questions ? (section.q || section.questions).map(q => ({
                                    id: q.id,
                                    type: q.type,
                                    text: q.text,
                                    imageUrl: q.imageUrl || '',
                                    problemDescription: q.pd || q.problemDescription || '',
                                    instructions: q.inst || q.instructions || '',
                                    boilerplateCode: q.bc || q.boilerplateCode || '',
                                    evaluationKeywords: q.ek || q.evaluationKeywords || '',
                                    evaluationPoints: (() => {
                                        // Combine evaluationPoints and evaluationKeywords for backward compatibility
                                        const evalPoints = q.evaluationPoints || '';
                                        const evalKeywords = q.ek || q.evaluationKeywords || '';
                                        if (evalPoints && evalKeywords) {
                                            // Both exist - combine them (remove duplicates)
                                            const pointsArray = typeof evalPoints === 'string' 
                                                ? evalPoints.split(',').map(p => p.trim()).filter(Boolean)
                                                : Array.isArray(evalPoints) ? evalPoints : [];
                                            const keywordsArray = typeof evalKeywords === 'string'
                                                ? evalKeywords.split(',').map(k => k.trim()).filter(Boolean)
                                                : Array.isArray(evalKeywords) ? evalKeywords : [];
                                            const combined = [...pointsArray, ...keywordsArray];
                                            return [...new Set(combined)].join(', ');
                                        }
                                        return evalPoints || evalKeywords || '';
                                    })(), // CRITICAL: Load evaluationPoints (combines with evaluationKeywords for backward compatibility)
                                    enableAIEvaluation: q.ai !== undefined ? q.ai : (q.enableAIEvaluation || false),
                                    maxScore: q.maxScore,
                                    options: q.opts || q.options ? (q.opts || q.options).map(opt => ({
                                        id: opt.id,
                                        text: opt.t || opt.text,
                                        marks: opt.m !== undefined ? opt.m : (opt.marks || 0),
                                        isCorrect: opt.c !== undefined ? opt.c : (opt.isCorrect || false)
                                    })) : [],
                                    psycoAnswer: q.psycoAnswer || '',
                                    scenarioOptions: q.scenarioOptions ? q.scenarioOptions.map(opt => ({
                                      title: opt.title || '',
                                      content: opt.content || '',
                                      evaluationPoints: opt.evaluationPoints || []
                                    })) : []
                                })) : []
                            })) : []
                        };
                        
                        if (mountedRef.current) {
                            setExam(normalizedExam);
                            window.currentExam = normalizedExam;
                            
                            // Extract and set cheat detection settings from cached data
                            const cheatSettings = CheatDetectionConfig.getSettingsFromExam(cachedExam);
                            setCheatDetectionSettings(cheatSettings);
                            console.log(`🔒 Cheat detection settings loaded from cache:`, cheatSettings);
                            
                            // Test the settings immediately
                            console.log('🔒 Testing cached settings:');
                            console.log('🔒 Tab switch enabled:', CheatDetectionConfig.isFeatureEnabled(cheatSettings, 'enableTabSwitchDetection'));
                            console.log('🔒 Copy paste enabled:', CheatDetectionConfig.isFeatureEnabled(cheatSettings, 'enableCopyPasteDetection'));
                            console.log('🔒 Webcam enabled:', CheatDetectionConfig.isFeatureEnabled(cheatSettings, 'enableWebcamDetection'));
                        }
                        
                        // Initialize timer with cached data
                        const currentUserEmail = user?.email || sessionStorage.getItem("checkemail");
                        const storedUserEmail = sessionStorage.getItem("timer_user_email");
                        const storedTimer = parseInt(sessionStorage.getItem("exam_timer"));
                        const storedSeconds = parseInt(sessionStorage.getItem("exam_sec"));
                        
                        if (storedTimer >= 0 && storedSeconds >= 0 && storedUserEmail === currentUserEmail) {
                            if (mountedRef.current) {
                                setMinutes(storedTimer);
                                setSeconds(storedSeconds >= 0 ? storedSeconds : 0);
                                setExamReady(true);
                            }
                        } else {
                            const examTimer = parseInt(sessionStorage.getItem("exam_timer"));
                            if (examTimer && examTimer >= 0) {
                                if (mountedRef.current) {
                                    setMinutes(examTimer);
                                    setSeconds(0);
                                    sessionStorage.setItem("exam_timer", examTimer.toString());
                                    sessionStorage.setItem("exam_sec", "0");
                                    sessionStorage.setItem("timer_user_email", currentUserEmail);
                                    setExamReady(true);
                                }
                            }
                        }
                        
                        if (mountedRef.current) {
                            setAnswers(prevAnswers => {
                                if (Object.keys(prevAnswers).length === 0) {
                                    const initialAnswers = {};
                                    normalizedExam.sections.forEach(section => {
                                        section.questions.forEach(question => {
                                            initialAnswers[question.id] = null;
                                        });
                                    });
                                    return initialAnswers;
                                }
                                return prevAnswers;
                            });
                        }
                        
                        setLoading(false);
                        return; // Exit early - no need to fetch from Firebase
                    }
                }
                
                // Just track the current user for timer validation (don't clear sessionStorage)
                const currentUserEmail = user?.email || sessionStorage.getItem("checkemail");
                const storedUserEmail = sessionStorage.getItem("timer_user_email");
                
                // Only set user email if not already set
                if (!storedUserEmail) {
                    sessionStorage.setItem("timer_user_email", currentUserEmail);
                }
                
                // Check if user is authenticated before accessing Firebase
                if (!isLoadTesting) {
                    // Wait for authentication state to be resolved
                    const currentUser = firebase.auth().currentUser;
                    
                    if (!currentUser) {
                        // Try to get the user from auth state
                        await new Promise((resolve) => {
                            const unsubscribe = firebase.auth().onAuthStateChanged((user) => {
                                unsubscribe();
                                if (!user) {
                                    swal("Authentication Error", "Authentication is required to access the assessment.", "error");
                                    history.push("/login");
                                    return;
                                }
                                resolve();
                            });
                        });
                    } else {
                        // Ensure user has a valid token before accessing Firebase
                        try {
                            await currentUser.getIdToken(true); // Force token refresh
                        } catch (tokenError) {
                            swal("Authentication Error", "Authentication session expired. Please login again to continue.", "error");
                            history.push("/login");
                            return;
                        }
                    }
                }

                // Initialize ShortUrlService for legacy short URL conversion
                const firebaseConfig = firebase.app().options;
                ShortUrlService.autoInit(firebaseConfig);

                // Fetch exam data from Firebase (same for both load testing and regular mode)
                const examRef = firebase.database().ref(`TestPapers/${examCode}`);
                
                const snapshot = await examRef.once('value');
                const examData = snapshot.val();
                
                // Calculate data usage for cost monitoring
                const calculateDataUsage = (data) => {
                    if (!data) return { size: 0, details: {} };
                    
                    const examJson = JSON.stringify(data);
                    const examSize = examJson.length;
                    
                    // Calculate image sizes
                    let totalImageSize = 0;
                    let imageCount = 0;
                    let imageDetails = [];
                    
                    if (data.sections) {
                        data.sections.forEach((section, sIdx) => {
                            if (section.q || section.questions) {
                                const questions = section.q || section.questions;
                                questions.forEach((question, qIdx) => {
                                    if (question.imageUrl) {
                                        imageCount++;
                                        // Estimate image size (rough calculation)
                                        const imageSize = question.imageUrl.length * 0.75; // Base64 is ~33% larger
                                        totalImageSize += imageSize;
                                        imageDetails.push({
                                            section: section.t || section.title || `Section ${sIdx + 1}`,
                                            question: qIdx + 1,
                                            imageUrl: question.imageUrl,
                                            estimatedSize: imageSize
                                        });
                                    }
                                });
                            }
                        });
                    }
                    
                    const totalSize = examSize + totalImageSize;
                    
                    return {
                        examSize: examSize,
                        imageSize: totalImageSize,
                        totalSize: totalSize,
                        imageCount: imageCount,
                        imageDetails: imageDetails,
                        formatted: {
                            examSize: (examSize / 1024).toFixed(2),
                            imageSize: (totalImageSize / 1024).toFixed(2),
                            totalSize: (totalSize / 1024).toFixed(2)
                        }
                    };
                };
                
                const dataUsage = calculateDataUsage(examData);
                
                // Log data usage for cost monitoring (commented out for production)
                // console.log('📊 Exam Data Usage Report:', {
                //     examCode: examCode,
                //     examSize: `${dataUsage.formatted.examSize} KB`,
                //     imageSize: `${dataUsage.formatted.imageSize} KB`,
                //     totalSize: `${dataUsage.formatted.totalSize} KB`,
                //     imageCount: dataUsage.imageCount,
                //     details: dataUsage.imageDetails
                // });
                
                if (examData && (examData.isDynamic || examData.dynamic)) { // Handle both old and new formats
                    // Normalize exam data to handle both old and new field names
                    const normalizedExam = {
                        examCode: examCode, // Add the exam code from URL params
                        examTitle: examData.title || examData.examTitle,
                        sections: examData.sections ? examData.sections.map(section => ({
                            id: section.id,
                            title: section.t || section.title, // Handle shortened field
                            weightage: section.w || section.weightage, // Handle shortened field
                            questions: section.q || section.questions ? (section.q || section.questions).map(q => ({
                                id: q.id,
                                type: q.type,
                                text: q.text,
                                imageUrl: q.imageUrl || '', // Keep original URL, let AuthenticatedImage handle conversion
                                problemDescription: q.pd || q.problemDescription || '', // Handle shortened field
                                instructions: q.inst || q.instructions || '', // Handle shortened field
                                boilerplateCode: q.bc || q.boilerplateCode || '', // Handle shortened field
                                evaluationKeywords: q.ek || q.evaluationKeywords || '', // Handle shortened field
                                evaluationPoints: (() => {
                                    // Combine evaluationPoints and evaluationKeywords for backward compatibility
                                    const evalPoints = q.evaluationPoints || '';
                                    const evalKeywords = q.ek || q.evaluationKeywords || '';
                                    if (evalPoints && evalKeywords) {
                                        // Both exist - combine them (remove duplicates)
                                        const pointsArray = typeof evalPoints === 'string' 
                                            ? evalPoints.split(',').map(p => p.trim()).filter(Boolean)
                                            : Array.isArray(evalPoints) ? evalPoints : [];
                                        const keywordsArray = typeof evalKeywords === 'string'
                                            ? evalKeywords.split(',').map(k => k.trim()).filter(Boolean)
                                            : Array.isArray(evalKeywords) ? evalKeywords : [];
                                        const combined = [...pointsArray, ...keywordsArray];
                                        return [...new Set(combined)].join(', ');
                                    }
                                    return evalPoints || evalKeywords || '';
                                })(), // CRITICAL: Load evaluationPoints (combines with evaluationKeywords for backward compatibility)
                                enableAIEvaluation: q.ai !== undefined ? q.ai : (q.enableAIEvaluation || false), // Handle shortened field
                                maxScore: q.maxScore, // Ensure maxScore is set if present in DB
                                options: q.opts || q.options ? (q.opts || q.options).map(opt => ({
                                    id: opt.id,
                                    text: opt.t || opt.text, // Handle shortened field
                                    marks: opt.m !== undefined ? opt.m : (opt.marks || 0), // Handle shortened field
                                    isCorrect: opt.c !== undefined ? opt.c : (opt.isCorrect || false) // Handle shortened field
                                })) : [],
                    
                                psycoAnswer: q.psycoAnswer || '',
                                scenarioOptions: q.scenarioOptions ? q.scenarioOptions.map(opt => ({
                                  title: opt.title || '',
                                  content: opt.content || '',
                                  evaluationPoints: opt.evaluationPoints || []
                                })) : []
                            })) : []
                        })) : []
                    };
                    

                    
                    if (mountedRef.current) {
                        setExam(normalizedExam);
                        // Store exam globally for getDisplayUrl function to access
                        window.currentExam = normalizedExam;
                        
                        // Extract and set cheat detection settings
                        const cheatSettings = CheatDetectionConfig.getSettingsFromExam(examData);
                        setCheatDetectionSettings(cheatSettings);
                        console.log(`🔒 Cheat detection settings loaded:`, cheatSettings);
                        
                        // Test the settings immediately
                        console.log('🔒 Testing settings:');
                        console.log('🔒 Tab switch enabled:', CheatDetectionConfig.isFeatureEnabled(cheatSettings, 'enableTabSwitchDetection'));
                        console.log('🔒 Copy paste enabled:', CheatDetectionConfig.isFeatureEnabled(cheatSettings, 'enableCopyPasteDetection'));
                        console.log('🔒 Webcam enabled:', CheatDetectionConfig.isFeatureEnabled(cheatSettings, 'enableWebcamDetection'));
                        
                        // Cache the exam data for future use
                        ExamCacheService.setExamCache(examCode, examData);
                        console.log(`💾 Exam data cached: ${examCode}`);
                    }
                    
                    // Initialize timer with proper values after exam is loaded
                    const currentUserEmail = user?.email || sessionStorage.getItem("checkemail");
                    const storedUserEmail = sessionStorage.getItem("timer_user_email");
                    const storedTimer = parseInt(sessionStorage.getItem("exam_timer"));
                    const storedSeconds = parseInt(sessionStorage.getItem("exam_sec"));
                    

                    
                    // Check if we have valid stored timer values for the current user
                    if (storedTimer >= 0 && storedSeconds >= 0 && storedUserEmail === currentUserEmail) {
                        if (mountedRef.current) {
                            setMinutes(storedTimer);
                            setSeconds(storedSeconds >= 0 ? storedSeconds : 0); // Use stored seconds or default to 0
                            setExamReady(true);
                        }
                    } else {
                        // Don't clear sessionStorage - just check if timer exists
                        
                        // Get timer from sessionStorage (set by formvalid from Firebase)
                        const examTimer = parseInt(sessionStorage.getItem("exam_timer"));
                        if (examTimer && examTimer >= 0) {
                            if (mountedRef.current) {
                                setMinutes(examTimer);
                                setSeconds(0); // Start with 0 seconds for real clock scenario
                                // Update sessionStorage with the actual timer values
                                sessionStorage.setItem("exam_timer", examTimer.toString());
                                sessionStorage.setItem("exam_sec", "0");
                                sessionStorage.setItem("timer_user_email", currentUserEmail);
                                setExamReady(true);
                            }
                        } else {
                            // Wait for timer to be set (retry mechanism)
                            const waitForTimer = () => {
                                const retryTimer = parseInt(sessionStorage.getItem("exam_timer"));
                                if (retryTimer && retryTimer >= 0) {
                                    setMinutes(retryTimer);
                                    setSeconds(0);
                                    sessionStorage.setItem("exam_timer", retryTimer.toString());
                                    sessionStorage.setItem("exam_sec", "0");
                                    sessionStorage.setItem("timer_user_email", currentUserEmail);
                                    setExamReady(true);
                                } else {
                                    // Show error and redirect to formvalid
                                    swal("Timer Error", "Unable to initialize exam timer. Please try again.", "error")
                                        .then(() => {
                                            history.push('/formvalid');
                                        });
                                }
                            };
                            
                            // Retry after a short delay
                            setTimeout(waitForTimer, 1000);
                            return; // Don't set examReady yet
                        }
                    }
                    
                    if (mountedRef.current) {
                        setAnswers(prevAnswers => {
                            if (Object.keys(prevAnswers).length === 0) {
                                const initialAnswers = {};
                                normalizedExam.sections.forEach(section => {
                                    section.questions.forEach(question => {
                                        initialAnswers[question.id] = null;
                                    });
                                });
                                return initialAnswers;
                            }
                            return prevAnswers;
                        });
                    }
                } else {
                }
            } catch (error) {
                if (isLoadTesting) {
                    // For load testing, show error but don't crash
                    swal("Load Testing Error", "Unable to retrieve assessment data. Please try again.", "error");
                } else {
                    swal("Error", "Unable to retrieve assessment data. Please check your connection and try again.", "error");
                }
            } finally {
                if (mountedRef.current) {
                    setLoading(false);
                }
            }
        };

        loadExamData();

        // Add event listener for timer expiration from fullscreen alerts
        const handleExamTimeUp = () => {
            // Auto-submit exam when time is up from any component
            if (autoSubmitRef.current) {
                autoSubmitRef.current();
            }
        };
        
        window.addEventListener('examTimeUp', handleExamTimeUp);

        // Check for auto-submit flag from other components
        const checkAutoSubmitFlag = () => {
            const autoSubmitRequired = sessionStorage.getItem('autoSubmitRequired');
            if (autoSubmitRequired === 'true') {
                sessionStorage.removeItem('autoSubmitRequired');
                sessionStorage.removeItem('autoSubmitTimestamp');
                if (autoSubmitRef.current) {
                    autoSubmitRef.current();
                }
            }
        };

        // Check immediately and then every second
        checkAutoSubmitFlag();
        const flagCheckInterval = setInterval(checkAutoSubmitFlag, 1000);

        // Update cheat counts every 3 seconds for better performance
        const cheatCountInterval = setInterval(() => {
            // Check if component is still mounted before updating state
            if (!mountedRef.current || !cheatDetectionSettings) {
                return;
            }
            
            // Get enabled cheat count keys
            const enabledKeys = CheatDetectionConfig.getCheatCountKeys(cheatDetectionSettings);
            
            // Build cheat counts object with only enabled features
            const newCheatCounts = {};
            
            // Always include all keys for compatibility, but only track enabled ones
            newCheatCounts.alt = enabledKeys.altKey ? parseInt(sessionStorage.getItem("countalt") || "0") : 0;
            newCheatCounts.ctrl = enabledKeys.ctrlKey ? parseInt(sessionStorage.getItem("countctrl") || "0") : 0;
            newCheatCounts.tab = enabledKeys.tabSwitch ? parseInt(sessionStorage.getItem("count_tabchange") || "0") : 0;
            newCheatCounts.face = parseInt(sessionStorage.getItem("count_facedetect") || "0"); // Always track face detection
            newCheatCounts.fullscreen = enabledKeys.fullscreen ? parseInt(sessionStorage.getItem("count_fullscreen") || "0") : 0;
            newCheatCounts.copyPaste = enabledKeys.copyPaste ? parseInt(sessionStorage.getItem("count_copypaste") || "0") : 0;
            newCheatCounts.webcamOff = enabledKeys.webcamOff ? parseInt(sessionStorage.getItem("count_webcamoff") || "0") : 0;
            newCheatCounts.contextMenu = enabledKeys.contextMenu ? parseInt(sessionStorage.getItem("count_contextmenu") || "0") : 0;
            newCheatCounts.noPerson = enabledKeys.noPerson ? parseInt(sessionStorage.getItem("count_noPerson") || "0") : 0;
            newCheatCounts.multiplePersons = enabledKeys.multiplePersons ? parseInt(sessionStorage.getItem("count_multiplePersons") || "0") : 0;
            newCheatCounts.suspiciousObjects = enabledKeys.suspiciousObjects ? parseInt(sessionStorage.getItem("count_suspiciousObjects") || "0") : 0;
            newCheatCounts.sidePanel = enabledKeys.sidePanel ? parseInt(sessionStorage.getItem("count_sidepanel") || "0") : 0;
            newCheatCounts.focusLoss = enabledKeys.focusLoss ? parseInt(sessionStorage.getItem("count_focusloss") || "0") : 0;

            setCheatCounts(newCheatCounts);
        }, 3000); // Increased from 1000ms to 3000ms for better performance

        

        // DetectRTC logic for webcam detection (skip in load testing)
        if (!isLoadTesting) {
            // Use a more efficient webcam detection approach
            const checkWebcam = async () => {
                try {
                    const stream = await navigator.mediaDevices.getUserMedia({ 
                        video: { 
                            width: { ideal: 640 },
                            height: { ideal: 480 }
                        } 
                    });
                    // Stop the stream immediately to free up resources
                    stream.getTracks().forEach(track => track.stop());
                } catch (err) {
                    // Webcam access denied or not available - this is normal
                }
            };
            
            // Check webcam asynchronously without blocking the UI
            setTimeout(checkWebcam, 1000); // Increased delay to prevent permission errors
        }



        return () => {
            // Mark component as unmounted to prevent state updates
            mountedRef.current = false;
            
            // Clear all intervals to prevent memory leaks
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
            if (cheatCountInterval) {
                clearInterval(cheatCountInterval);
            }
            if (flagCheckInterval) {
                clearInterval(flagCheckInterval);
            }
            
            // Remove event listeners
            window.removeEventListener('examTimeUp', handleExamTimeUp);
            
            // Don't clear sessionStorage on component unmount - let it persist
            
            // Cleanup security system
            if (securityRef.current) {
                securityRef.current.cleanup();
            }
            
            // Remove global security reference
            if (window.examSecurity) {
                delete window.examSecurity;
            }
        };
    }, [examCode, history]);
    
    const handleAnswerChange = useCallback((questionId, answer) => {
        // Find the question to determine its type
        let questionType = null;
        exam.sections.forEach(section => {
            const question = section.questions.find(q => q.id === questionId);
            if (question) {
                questionType = question.type;
            }
        });

        // Handle multi-choice questions - ensure answer is stored as array
        let processedAnswer = answer;
        if (questionType === 'mcq-multi') {
            if (typeof answer === 'string' && answer) {
                // Convert string to array for multi-choice
                processedAnswer = [answer];
            } else if (Array.isArray(answer)) {
                // Already an array, keep as is
                processedAnswer = answer;
            } else {
                // Invalid answer, set as empty array
                processedAnswer = [];
            }
        }

        setAnswers(prev => ({ ...prev, [questionId]: processedAnswer }));
        setQuestionErrors(prevErrors => {
            const newErrors = { ...prevErrors };
            
            // Handle different answer types for validation
            let isValid = false;
            
            if (processedAnswer && typeof processedAnswer === 'object' && processedAnswer.text !== undefined) {
                // Rich text answer with PDF attachment
                const hasText = processedAnswer.text && processedAnswer.text.trim() !== '';
                // Check for PDF file (either file object or uploaded URL)
                const hasPdfFile = processedAnswer.pdfFile && (processedAnswer.pdfFile.file || processedAnswer.pdfFile.url || processedAnswer.pdfFile.fileName);
                isValid = hasText || hasPdfFile;
            } else if (typeof processedAnswer === 'string') {
                // Simple string answer
                isValid = processedAnswer && processedAnswer.trim() !== '';
            } else if (Array.isArray(processedAnswer)) {
                // Array answer (for MCQs)
                isValid = processedAnswer.length > 0;
            } else {
                // Other types
                isValid = processedAnswer !== null && processedAnswer !== undefined;
            }
            
            if (isValid) {
                delete newErrors[questionId];
            }
            
            return newErrors;
        });
    }, [exam]);
    
  // Function to convert video path to URL for display
  const getVideoDisplayUrl = async (videoPathOrUrl) => {
    if (!videoPathOrUrl) return null;
    
    // If it's already a full URL, return as-is
    if (videoPathOrUrl.startsWith('http')) {
      return videoPathOrUrl;
    }
    
    // If it's a relative path, add 'submissions/' prefix and convert to full Firebase Storage URL
    try {
      const fullPath = `submissions/${videoPathOrUrl}`;
      const storageRef = firebase.storage().ref(fullPath);
      return await storageRef.getDownloadURL();
    } catch (error) {
      console.error('Failed to get video URL from path:', error);
      return videoPathOrUrl; // Return original path as fallback
    }
  };

  // Function to clean data for Firebase (remove undefined values and fix NaN values)
  const cleanFirebaseData = (obj) => {
        if (obj === null || obj === undefined) {
            return null;
        }
        
        if (typeof obj !== 'object') {
            // Handle NaN values for primitive types
            if (typeof obj === 'number' && isNaN(obj)) {
                return 0;
            }
            return obj;
        }
        
        if (Array.isArray(obj)) {
            return obj.map(item => cleanFirebaseData(item)).filter(item => item !== null);
        }
        
        const cleaned = {};
        for (const [key, value] of Object.entries(obj)) {
            if (value !== undefined) {
                // Handle NaN values in nested objects
                if (typeof value === 'number' && isNaN(value)) {
                    cleaned[key] = 0;
                } else {
                    cleaned[key] = cleanFirebaseData(value);
                }
            }
        }
        return cleaned;
    };

    const calculateProvisionalScore = () => {
      let score = 0;
      let maxScore = 0;

      exam.sections.forEach((section, sIdx) => {
        section.questions.forEach((question, qIdx) => {
          const answer = answers[question.id];
          
          if (question.type === 'mcq-single' && answer) {
            const selectedOption = question.options.find(opt => opt.id === answer);
            if(selectedOption && selectedOption.isCorrect) {
              const marks = Number(selectedOption.marks) || 1;
              score += marks;
            }
          } else if (question.type === 'mcq-multi') {
            if (Array.isArray(answer) && answer.length > 0) {
              answer.forEach((optionId, index) => {
                const selectedOption = question.options.find(opt => opt.id === optionId);
                if(selectedOption && selectedOption.isCorrect) {
                  const marks = Number(selectedOption.marks) || 1;
                  score += marks;
                }
              });
            } else if (typeof answer === 'string' && answer) {
              const selectedOption = question.options.find(opt => opt.id === answer);
              if(selectedOption && selectedOption.isCorrect) {
                const marks = Number(selectedOption.marks) || 1;
                score += marks;
              }
            }
          }
          
          // Calculate max score for MCQs
          if (question.type === 'mcq-single' || question.type === 'mcq-multi') {
            question.options.forEach(opt => {
                if(opt.isCorrect) {
                  const marks = Number(opt.marks) || 1;
                  maxScore += marks;
                }
            });
          }
        });
      });
      

      return { score, maxScore };
    };

    // Auto-submit function (no validation)
    // options.terminated - assessment was ended early (security warning limit reached)
    const autoSubmitExam = async (options = {}) => {
        const { terminated = false, reason = '' } = options || {};
        const endTitle = terminated ? "Assessment Ended" : "Time's up!";
        const endText = terminated
            ? "Your assessment has been ended due to repeated security violations and your responses have been submitted."
            : "Your assessment has been auto-submitted.";

        // Progress feedback for the terminated flow (no-op for the normal timer auto-submit)
        const showTerminationProgress = (currentStep, progress, extra = {}) => {
            if (!terminated) return;
            setSubmissionProgress(prev => ({
                ...prev,
                open: true,
                currentStep,
                progress,
                status: 'processing',
                errorMessage: '',
                ...extra
            }));
        };

        // Prevent multiple auto-submits
        if (isSubmitting) {
            console.warn(`Auto-submit skipped - a submission is already running (terminated: ${terminated})`);
            return;
        }

        // Check if exam data is available
        if (!exam || !exam.sections || exam.sections.length === 0) {
            // Still redirect to thank you page even if no exam data
            history.push('/thankyou');
            return;
        }

        showTerminationProgress(0, 20);

        // Save all unsaved videos before auto-submission. Bounded, so a stalled upload can
        // never leave the candidate waiting on a submission that silently never happens.
        try {
            await Promise.race([
                saveAllVideosForSubmission(),
                new Promise((_, reject) =>
                    setTimeout(() => reject(new Error('Video upload timed out')), VIDEO_SAVE_TIMEOUT_MS)
                )
            ]);
            console.log('All videos uploaded successfully for auto-submit');
        } catch (error) {
            console.error('Failed to save videos during auto-submit:', error);
            // Continue with submission even if video save fails
        }

        showTerminationProgress(1, 60);

        setIsSubmitting(true);
        setShowLoader(true);
        try {
            // For load testing, save to separate collection
            if (isLoadTesting) {
                const userEmail = user?.email || sessionStorage.getItem("checkemail") || 'load-test-user';
                const sanitizedEmail = userEmail.replace(/\./g, ',');
                const uniqueEmail = `${sanitizedEmail}_${Date.now()}`;
                const loadTestRef = firebase.database().ref(`Load_Testing/${examCode}/${uniqueEmail}`);
                const loadTestData = {
                    examCode,
                    name: userName,
                    email: userEmail,
                    submittedAt: firebase.database.ServerValue.TIMESTAMP,
                    answers: answers,
                    provisionalScore: calculateProvisionalScore(),
                    isLoadTest: true,
                    loadTestTimestamp: Date.now(),
                    isAutoSubmitted: true,
                    isTerminated: terminated,
                    terminationReason: terminated ? reason : '',
                    totalWarnings: warningCountRef.current,
                    cheatCounts: cheatCounts,
                    totalCheatCount: Object.values(cheatCounts).reduce((sum, count) => sum + count, 0)
                };

                try {
                    await loadTestRef.set(loadTestData);
                    setShowLoader(false);
                    swal("Load Testing - Auto Submit", "Assessment auto-submitted for load testing! Database performance tested.", "info")
                        .then(() => {
                            history.push('/thankyou');
                        });
                } catch (saveError) {
                    setShowLoader(false);
                    swal("Error", "There was an issue auto-submitting your assessment.", "error");
                }
                return;
            }
            
            const userEmail = user?.email || sessionStorage.getItem("checkemail") || 'unknown';
            const sanitizedEmail = userEmail.replace(/\./g, ',');
            const submissionRef = firebase.database().ref(`Exam_results/${examCode}/${sanitizedEmail}`);
            const finalAnswers = { ...answers };
            let provisionalScore = calculateProvisionalScore();
            
            // Validate provisional score
            if (!provisionalScore || typeof provisionalScore.score !== 'number' || typeof provisionalScore.maxScore !== 'number') {
                // Set default values if calculation fails
                provisionalScore = { score: 0, maxScore: 0 };
            }
            
            // Handle file uploads, large text content, and rich text answers with images
            for (const questionId in finalAnswers) {
                const answer = finalAnswers[questionId];
                if (answer instanceof File) {
                    const storageRef = firebase.storage().ref(`submissions/${examCode}/${sanitizedEmail}/${questionId}/${answer.name}`);
                    const uploadTask = await storageRef.put(answer);
                    const downloadURL = await uploadTask.ref.getDownloadURL();
                    finalAnswers[questionId] = downloadURL;
                } else if (answer && typeof answer === 'object' && answer.text !== undefined) {
                    // Handle rich text answers with images
                    const richTextAnswer = { ...answer };
                    
                    // If text is too long, store it in Firebase Storage
                    if (richTextAnswer.text && richTextAnswer.text.length > 10000) {
                        const textBlob = new Blob([richTextAnswer.text], { type: 'text/plain' });
                        const storageRef = firebase.storage().ref(`submissions/${examCode}/${sanitizedEmail}/${questionId}/answer.txt`);
                        const uploadTask = await storageRef.put(textBlob);
                        const downloadURL = await uploadTask.ref.getDownloadURL();
                        richTextAnswer.text = {
                            type: 'storage_reference',
                            url: downloadURL,
                            size: richTextAnswer.text.length
                        };
                    }
                    
                    // Images are already uploaded and have URLs, so we just store the metadata
                    if (richTextAnswer.images && richTextAnswer.images.length > 0) {
                        richTextAnswer.images = richTextAnswer.images.map(img => ({
                            id: img.id,
                            url: img.url,
                            caption: img.caption,
                            fileName: img.fileName,
                            uploadTime: img.uploadTime
                        }));
                    }
                    
                    finalAnswers[questionId] = richTextAnswer;
                } else if (typeof answer === 'string' && answer.length > 10000) {
                    const textBlob = new Blob([answer], { type: 'text/plain' });
                    const storageRef = firebase.storage().ref(`submissions/${examCode}/${sanitizedEmail}/${questionId}/answer.txt`);
                    const uploadTask = await storageRef.put(textBlob);
                    const downloadURL = await uploadTask.ref.getDownloadURL();
                    finalAnswers[questionId] = {
                        type: 'storage_reference',
                        url: downloadURL,
                        size: answer.length
                    };
                }
            }
            // Get cheat counts from session storage
            const cheatCounts = {
                alt: sessionStorage.getItem("countalt") || "0",
                ctrl: sessionStorage.getItem("countctrl") || "0",
                tab: sessionStorage.getItem("count_tabchange") || "0",
                face: sessionStorage.getItem("count_facedetect") || "0",
                fullscreen: sessionStorage.getItem("count_fullscreen") || "0",
                copyPaste: sessionStorage.getItem("count_copypaste") || "0",
                webcamOff: sessionStorage.getItem("count_webcamoff") || "0",
                contextMenu: sessionStorage.getItem("count_contextmenu") || "0",
                noPerson: sessionStorage.getItem("count_noPerson") || "0",
                multiplePersons: sessionStorage.getItem("count_multiplePersons") || "0",
                suspiciousObjects: sessionStorage.getItem("count_suspiciousObjects") || "0",
                sidePanel: sessionStorage.getItem("count_sidepanel") || "0",
                focusLoss: sessionStorage.getItem("count_focusloss") || "0"
            };

            // Calculate total cheat count (only main violations: copy/paste, tab switch, webcam)
            const totalCheatCount = parseInt(cheatCounts.copyPaste) + parseInt(cheatCounts.tab) + parseInt(cheatCounts.webcamOff);

            const submissionData = {
                examCode,
                name: userName,
                email: userEmail,
                submittedAt: firebase.database.ServerValue.TIMESTAMP,
                answers: finalAnswers,
                provisionalScore: provisionalScore,
                isDynamic: true,
                isTerminated: terminated,
                terminationReason: terminated ? reason : '',
                totalWarnings: warningCountRef.current,
                ...cheatCounts,
                totalCheatCount
            };
            try {
                await submissionRef.set(submissionData);
                console.log(`✅ Auto-submission saved for ${userEmail} (terminated: ${terminated})`);

                // AI evaluation is slow - let it finish in the background and update the record
                const evaluationPromise = AIEvaluationService.evaluateSubmission(exam, submissionData)
                    .then(evaluationResult => {
                        submissionRef.update({ aiEvaluation: evaluationResult });
                        return true;
                    })
                    .catch((aiError) => {
                        console.error('AI evaluation failed after auto-submit:', aiError);
                        return false;
                    });

                if (terminated) {
                    // Responses are already stored safely - close the assessment straight away
                    // instead of holding the candidate on screen until the AI evaluation returns
                    setShowLoader(false);
                    showTerminationProgress(2, 100, { status: 'success' });
                    setTimeout(() => {
                        setSubmissionProgress(prev => ({ ...prev, open: false }));
                        history.push('/thankyou');
                    }, 2000);
                    return;
                }

                evaluationPromise.then((evaluated) => {
                    setShowLoader(false);
                    swal(endTitle, evaluated ? `${endText} Results are being processed.` : endText, "info")
                        .then(() => {
                            history.push('/thankyou');
                        });
                });
            } catch (saveError) {
                setShowLoader(false);
                console.error('❌ Auto-submission failed to save:', saveError);

                if (terminated) {
                    // Never fail silently here - the candidate cannot continue, so show what went wrong
                    setSubmissionProgress(prev => ({
                        ...prev,
                        open: true,
                        status: 'error',
                        errorMessage: `We could not save your responses (${saveError.code || saveError.message || 'unknown error'}). Please contact your assessment coordinator.`
                    }));
                    return;
                }

                // Only show error popup for specific critical errors
                if (saveError.code === 'PERMISSION_DENIED' || saveError.code === 'UNAUTHENTICATED') {
                    swal("Authentication Error", "Your session has expired. Please login again.", "error")
                        .then(() => {
                            history.push('/login');
                        });
                } else if (saveError.code === 'QUOTA_EXCEEDED') {
                    swal("System Error", "System is temporarily unavailable. Please try again later.", "error")
                        .then(() => {
                            history.push('/thankyou');
                        });
                } else {
                    // For other save errors, just redirect to thank you page without showing error
                    history.push('/thankyou');
                }
            }
        } catch (error) {
            setShowLoader(false);
            console.error('❌ Auto-submission failed:', error);

            if (terminated) {
                // Never fail silently here - the candidate cannot continue, so show what went wrong
                setSubmissionProgress(prev => ({
                    ...prev,
                    open: true,
                    status: 'error',
                    errorMessage: `We could not submit your assessment (${error.code || error.message || 'unknown error'}). Please contact your assessment coordinator.`
                }));
                return;
            }

            // Only show error popup for specific critical errors
            if (error.code === 'PERMISSION_DENIED' || error.code === 'UNAUTHENTICATED') {
                swal("Authentication Error", "Your session has expired. Please login again.", "error")
                    .then(() => {
                        history.push('/login');
                    });
            } else if (error.code === 'QUOTA_EXCEEDED') {
                swal("System Error", "System is temporarily unavailable. Please try again later.", "error")
                    .then(() => {
                        history.push('/thankyou');
                    });
            } else {
                // For other errors, just redirect to thank you page without showing error
                history.push('/thankyou');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    // Assign autoSubmitExam to ref so it can be called from event listeners
    autoSubmitRef.current = autoSubmitExam;
            // console.log("🔗 REF ASSIGNMENT: autoSubmitRef.current assigned", typeof autoSubmitRef.current);
    
    // Also expose on window object for direct access from other components
    window.autoSubmitExam = autoSubmitExam;
            // console.log("🌐 WINDOW ASSIGNMENT: window.autoSubmitExam assigned");

    const handleSubmit = async () => {
        // Pause security alerts immediately when submit is clicked
        if (securityRef.current) {
            securityRef.current.pauseSecurityAlerts();
        }

        // Videos will be saved during the submission process with progress tracking
        
        // For load testing, bypass validation but save to separate collection for performance testing
        if (isLoadTesting) {
            try {
                // Save load testing data to separate collection for database performance testing
                const userEmail = user?.email || sessionStorage.getItem("checkemail") || 'load-test-user';
                const sanitizedEmail = userEmail.replace(/\./g, ',');
                const uniqueEmail = `${sanitizedEmail}_${Date.now()}`;
                const loadTestRef = firebase.database().ref(`Load_Testing/${examCode}/${uniqueEmail}`);
                const loadTestData = {
                    examCode,
                    name: userName,
                    email: userEmail,
                    submittedAt: firebase.database.ServerValue.TIMESTAMP,
                    answers: answers,
                    provisionalScore: calculateProvisionalScore(),
                    isLoadTest: true,
                    loadTestTimestamp: Date.now(),
                    cheatCounts: cheatCounts,
                    totalCheatCount: Object.values(cheatCounts).reduce((sum, count) => sum + count, 0)
                };
                
                await loadTestRef.set(loadTestData);
                swal("Load Testing Mode", "Assessment submitted successfully for load testing! Database performance tested.", "success").then(() => {
                    history.push('/thankyou');
                });
            } catch (error) {
                // Even if save fails, continue with load testing
                swal("Load Testing Mode", "Assessment submitted successfully for load testing!", "success").then(() => {
                    history.push('/thankyou');
                });
            }
            return;
        }
        
        // Validation: check all questions are answered
        const errors = {};
        const unansweredNumbers = [];
        
        exam.sections.forEach((section, sIdx) => {
            section.questions.forEach((question, qIdx) => {
                const ans = answers[question.id];
                let isValid = false;
                
                console.log(`Validating question ${qIdx + 1} (${question.id}):`, {
                    type: question.type,
                    hasAnswer: !!ans,
                    answer: ans
                });
                
                if (['text', 'scenario-text', 'code'].includes(question.type)) {
                    // Special handling for coding questions
                    if (question.type === 'code') {
                        if (ans && typeof ans === 'object' && ans.code !== undefined) {
                            isValid = ans.code && ans.code.trim() !== '';
                        }
                    } else {
                        // For text and scenario-text questions, check if there's text content or PDF attachment
                        if (ans && typeof ans === 'object' && ans.text !== undefined) {
                            const hasText = ans.text && ans.text.trim() !== '';
                            // Check for PDF file (either file object or uploaded URL)
                            const hasPdfFile = ans.pdfFile && (ans.pdfFile.file || ans.pdfFile.url || ans.pdfFile.fileName);
                            isValid = hasText || hasPdfFile;
                            
                            // For scenario-text, also check if a scenario is selected
                            if (question.type === 'scenario-text') {
                                const hasScenario = ans.selectedScenario && ans.selectedScenario.trim() !== '';
                                isValid = isValid && hasScenario;
                            }
                        } else if (typeof ans === 'string') {
                            isValid = ans && ans.trim() !== '';
                        }
                    }
                } else if (question.type === 'video') {
                    // For video questions, MANDATORY validation for manual submission
                    // Video must be recorded and saved before submission
                    console.log(`Video question ${question.id} validation:`, {
                        answer: ans,
                        hasAnswer: !!ans,
                        answerType: typeof ans,
                        hasVideoUrl: ans?.videoUrl,
                        hasVideoPath: ans?.videoPath,
                        videoUrlValue: ans?.videoUrl,
                        videoPathValue: ans?.videoPath
                    });
                    
                    // Check if there's a saved video answer (already uploaded)
                    if (ans && typeof ans === 'object') {
                        const hasVideoUrl = ans.videoUrl && ans.videoUrl.trim() !== '';
                        const hasVideoPath = ans.videoPath && ans.videoPath.trim() !== '';
                        isValid = hasVideoUrl || hasVideoPath;
                        console.log(`Video question ${question.id} validation result (saved):`, { hasVideoUrl, hasVideoPath, isValid });
                    } else {
                        // For manual submission, check if there's an unsaved video ready to be saved
                        const videoRecorderRef = videoRecorderRefs.current[question.id];
                        
                        if (videoRecorderRef) {
                            // Check if there's an unsaved video
                            const hasUnsavedVideo = videoRecorderRef.hasUnsavedVideo();
                            
                            // Additional check: verify there's actual video content
                            // Check sessionStorage for video data (including _exists flag for large videos)
                            const videoKey = `video_${examCode}_${question.id}`;
                            const hasVideoInStorage = sessionStorage.getItem(videoKey) !== null || 
                                                      sessionStorage.getItem(`${videoKey}_exists`) === 'true';
                            
                            // Video is valid if either:
                            // 1. There are recorded chunks in the recorder, OR
                            // 2. There's a video saved in sessionStorage (or reference flag for large videos)
                            isValid = hasUnsavedVideo || hasVideoInStorage;
                            
                            console.log(`Video question ${question.id} validation result (unsaved):`, { 
                                hasUnsavedVideo, 
                                hasVideoInStorage,
                                isValid,
                                hasVideoRecorderRef: !!videoRecorderRef
                            });
                        } else {
                            // No video recorder ref found - video definitely not recorded
                            isValid = false;
                            console.log(`Video question ${question.id} validation result: No video recorder ref found`);
                        }
                    }
                    
                    // FINAL CHECK: If still not valid, explicitly mark as invalid
                    if (!isValid) {
                        console.warn(`⚠️ Video question ${question.id} is INVALID - no video recorded`);
                    }
                } else {
                    // For other question types, use existing validation
                    isValid = ans !== null && ans !== undefined && 
                             ((typeof ans === 'string' && ans.trim() !== '') || 
                              (Array.isArray(ans) && ans.length > 0));
                }
                
                if (!isValid) {
                    if (question.type === 'video') {
                        errors[question.id] = 'Video recording is mandatory. Please record a video before submitting.';
                    } else {
                        errors[question.id] = 'Please answer this question before submitting.';
                    }
                    unansweredNumbers.push(`${qIdx + 1}`);
                }
                
                if (question.type === 'psyco') {
                    // Always check permutation of A, B, C, D for psyco questions
                    const ans = (answers[question.id] || '').toUpperCase();
                    if (!/^[ABCD]{4}$/.test(ans) || new Set(ans).size !== 4) {
                        errors[question.id] = 'Answer must be a permutation of A, B, C, D';
                        unansweredNumbers.push(`${qIdx + 1}`);
                    }
                }
            });
        });
        setQuestionErrors(errors);
        if (Object.keys(errors).length > 0) {
            // Resume security alerts if validation fails
            if (securityRef.current) {
                securityRef.current.resumeSecurityAlerts();
            }
            
            // Create section-wise missing questions summary
            const sectionMissingQuestions = {};
            exam.sections.forEach((section, sIdx) => {
                const missingInSection = [];
                section.questions.forEach((question, qIdx) => {
                    if (errors[question.id]) {
                        missingInSection.push({
                            number: qIdx + 1,
                            text: question.text,
                            type: question.type,
                            error: errors[question.id]
                        });
                    }
                });
                if (missingInSection.length > 0) {
                    sectionMissingQuestions[section.title] = missingInSection;
                }
            });
            
            // Create professional alert content
            const totalMissing = Object.values(sectionMissingQuestions).flat().length;
            const sectionsWithMissing = Object.keys(sectionMissingQuestions).length;
            
            let alertContent = `
                <div style="text-align: left; font-family: 'Segoe UI', sans-serif;">
                    <div style="margin-bottom: 16px; color: #d32f2f; font-weight: 600; font-size: 16px;">
                        📋 Incomplete Assessment Submission
                    </div>
                    <div style="margin-bottom: 12px; color: #333; font-size: 14px;">
                        Your assessment requires <strong>${totalMissing} unanswered question${totalMissing > 1 ? 's' : ''}</strong> across <strong>${sectionsWithMissing} section${sectionsWithMissing > 1 ? 's' : ''}</strong> before submission.
                    </div>
                    <div style="background: #f8f9fa; border: 1px solid #e9ecef; border-radius: 8px; padding: 12px; max-height: 300px; overflow-y: auto;">
            `;
            
            Object.entries(sectionMissingQuestions).forEach(([sectionTitle, questions], idx) => {
                alertContent += `
                    <div style="margin-bottom: ${idx > 0 ? '16px' : '0'};">
                        <div style="color: #1976d2; font-weight: 600; font-size: 14px; margin-bottom: 8px; border-bottom: 1px solid #e0e0e0; padding-bottom: 4px;">
                            📑 ${sectionTitle}
                        </div>
                        <div style="margin-left: 12px;">
                `;
                
                questions.forEach(q => {
                    const questionTypeIcon = {
                        'mcq-single': '🔘',
                        'mcq-multi': '☑️',
                        'text': '📝',
                        'scenario-text': '📋',
                        'code': '💻',
                        'video': '🎥',
                        'psyco': '🧠'
                    }[q.type] || '❓';
                    
                    // Highlight video questions as mandatory
                    const isVideoQuestion = q.type === 'video';
                    const questionStyle = isVideoQuestion 
                        ? 'font-size: 13px; color: #d32f2f; font-weight: 600; background: #ffebee; padding: 6px 8px; border-radius: 4px; border-left: 3px solid #d32f2f;'
                        : 'font-size: 13px; color: #555;';
                    
                    const mandatoryLabel = isVideoQuestion ? ' <span style="color: #d32f2f; font-size: 11px;">(MANDATORY)</span>' : '';
                    
                    alertContent += `
                        <div style="${questionStyle} margin-bottom: 6px;">
                            ${questionTypeIcon} Question ${q.number}${mandatoryLabel}
                            ${isVideoQuestion ? '<br/><span style="font-size: 11px; color: #b71c1c;">Video recording required</span>' : ''}
                        </div>
                    `;
                });
                
                alertContent += `
                        </div>
                    </div>
                `;
            });
            
            alertContent += `
                    </div>
                    <div style="margin-top: 12px; color: #666; font-size: 13px; font-style: italic;">
                        Please complete all required questions to proceed with your submission.
                        ${Object.values(sectionMissingQuestions).flat().some(q => q.type === 'video') 
                            ? '<br/><br/><strong style="color: #d32f2f;">⚠️ Note: Video questions are mandatory and must be recorded before submission.</strong>' 
                            : ''}
                    </div>
                </div>
            `;
            
            swal({
                title: "Assessment Incomplete",
                content: {
                    element: "div",
                    attributes: {
                        innerHTML: alertContent
                    }
                },
                icon: "warning",
                button: "Review Questions",
                className: "incomplete-submission-alert",
                closeOnClickOutside: false,
                closeOnEsc: false
            });
            
            setIsSubmitting(false);
            return;
        }

        // Check if there are video questions with unsaved videos
        const hasVideoQuestions = exam?.sections.some(section => 
            section.questions.some(question => question.type === 'video')
        );
        
        const hasUnsavedVideos = hasVideoQuestions && Object.values(videoRecorderRefs.current).some(ref => 
            ref && ref.hasUnsavedVideo && ref.hasUnsavedVideo()
        );

        // Dynamic submission steps based on exam content
        const submissionSteps = [
            'Validating answers...',
            ...(hasUnsavedVideos ? ['Uploading video recordings...'] : []),
            'Uploading files and images...',
            ...(hasVideoQuestions ? ['Processing video submissions...'] : []),
            'Saving submission data...',
            'Evaluating answers with AI...',
            'Finalizing results...'
        ];

        console.log('Submission steps:', {
            hasVideoQuestions,
            hasUnsavedVideos,
            totalSteps: submissionSteps.length,
            steps: submissionSteps
        });

        setSubmissionProgress({
            open: true,
            currentStep: 0,
            progress: 0,
            status: 'processing',
            errorMessage: ''
        });

        // Helper function to get step number and progress
        const getStepInfo = (stepName) => {
            const stepIndex = submissionSteps.findIndex(step => step === stepName);
            const totalSteps = submissionSteps.length;
            const progress = Math.round((stepIndex / totalSteps) * 100);
            return { stepIndex, progress };
        };

        setIsSubmitting(true);
        try {
            // Step 1: Validating answers (already done above)
            const validationStep = getStepInfo('Validating answers...');
            setSubmissionProgress(prev => ({
                ...prev,
                currentStep: validationStep.stepIndex,
                progress: validationStep.progress
            }));

            const userEmail = user?.email || sessionStorage.getItem("checkemail") || 'unknown';
            const sanitizedEmail = userEmail.replace(/\./g, ',');
            const submissionRef = firebase.database().ref(`Exam_results/${examCode}/${sanitizedEmail}`);
            
            const finalAnswers = { ...answers };
            let provisionalScore = calculateProvisionalScore();
            
            // Validate provisional score
            if (!provisionalScore || typeof provisionalScore.score !== 'number' || typeof provisionalScore.maxScore !== 'number') {
                // Set default values if calculation fails
                provisionalScore = { score: 0, maxScore: 0 };
            }

            // Step 2: Uploading video recordings (only if there are unsaved videos)
            if (hasUnsavedVideos) {
                const videoUploadStep = getStepInfo('Uploading video recordings...');
                setSubmissionProgress(prev => ({
                    ...prev,
                    currentStep: videoUploadStep.stepIndex,
                    progress: videoUploadStep.progress
                }));

                // Upload all unsaved videos first and wait for completion
                const uploadedVideoAnswers = await saveAllVideosForSubmission();
                console.log('All videos uploaded successfully');
                console.log('Uploaded video answers:', uploadedVideoAnswers);
                
                // Update finalAnswers with the uploaded video answers
                Object.keys(uploadedVideoAnswers).forEach(questionId => {
                    finalAnswers[questionId] = uploadedVideoAnswers[questionId];
                    console.log(`Added video answer to finalAnswers for question ${questionId}:`, uploadedVideoAnswers[questionId]);
                });
                
                // Also update the answers state for consistency
                setAnswers(prevAnswers => {
                    const updatedAnswers = { ...prevAnswers, ...uploadedVideoAnswers };
                    return updatedAnswers;
                });
            }

            // Step 3: Uploading files and images
            const filesUploadStep = getStepInfo('Uploading files and images...');
            setSubmissionProgress(prev => ({
                ...prev,
                currentStep: filesUploadStep.stepIndex,
                progress: filesUploadStep.progress
            }));

            // --- NEW: Upload images and PDFs for text answers only at submit time ---
            for (const questionId in finalAnswers) {
                const answer = finalAnswers[questionId];
                if (answer && typeof answer === 'object' && answer.text !== undefined) {
                    // Handle images
                    if (Array.isArray(answer.images) && answer.images.length > 0) {
                        // Only upload images that have a local file (not already uploaded)
                        const uploadedImages = [];
                        for (const img of answer.images) {
                            if (img.file) {
                                // Upload to Firebase Storage
                                const storageRef = firebase.storage().ref(`submissions/${examCode}/${sanitizedEmail}/${questionId}/${img.file.name}`);
                                const uploadTask = await storageRef.put(img.file);
                                const downloadURL = await uploadTask.ref.getDownloadURL();
                                uploadedImages.push({
                                    id: img.id,
                                    url: downloadURL,
                                    caption: img.caption,
                                    fileName: img.file.name,
                                    uploadTime: new Date().toISOString()
                                });
                            } else if (img.url) {
                                // Already uploaded (should not happen with new logic, but keep for safety)
                                uploadedImages.push(img);
                            }
                        }
                        answer.images = uploadedImages;
                    }
                    
                    // Handle PDF files (same pattern as video recordings)
                    if (answer.pdfFile && answer.pdfFile.file) {
                        try {
                            console.log(`Uploading PDF for question ${questionId}:`, answer.pdfFile.fileName);
                            
                            // Generate unique filename with timestamp (same as video recordings)
                            const timestamp = Date.now();
                            const randomId = Math.random().toString(36).substring(2, 15);
                            const originalFileName = answer.pdfFile.fileName;
                            const fileExtension = originalFileName.split('.').pop();
                            const uniqueFileName = `pdf_${timestamp}_${randomId}.${fileExtension}`;
                            
                            // Upload PDF to Firebase Storage (same path structure as videos)
                            const storageRef = firebase.storage().ref(`submissions/${examCode}/${questionId}/${uniqueFileName}`);
                            const uploadTask = await storageRef.put(answer.pdfFile.file);
                            
                            // Store only the path (same as video recordings)
                            const pdfPath = `${examCode}/${questionId}/${uniqueFileName}`;
                            
                            // Create new PDF object without losing original data
                            const uploadedPdfFile = {
                                fileName: uniqueFileName, // Use unique filename
                                originalFileName: originalFileName, // Keep original name for reference
                                pdfPath: pdfPath, // Only store the relative path, not full URL
                                uploadTime: new Date().toISOString(),
                                size: answer.pdfFile.file.size,
                                uploaded: true // Mark as successfully uploaded
                            };
                            
                            // Extract PDF text and add to existing object
                            try {
                                const PdfTextExtractionService = (await import('../services/PdfTextExtractionService')).default;
                                const extractionResult = await PdfTextExtractionService.extractText(answer.pdfFile.file);
                                if (extractionResult.text && extractionResult.text.trim()) {
                                    uploadedPdfFile.extractedText = extractionResult.text;
                                }
                            } catch (extractionError) {
                                console.error(`PDF text extraction failed:`, extractionError);
                                // Continue even if extraction fails - PDF is still uploaded
                            }
                            
                            // Only update if upload was successful
                            answer.pdfFile = uploadedPdfFile;
                            console.log(`PDF uploaded successfully for question ${questionId}:`, answer.pdfFile);
                        } catch (error) {
                            console.error(`Failed to upload PDF for question ${questionId}:`, error);
                            // CRITICAL: Keep the original PDF file object for retry
                            // Don't overwrite - just mark as failed
                            answer.pdfFile.uploadError = error.message;
                            answer.pdfFile.uploadFailed = true;
                            console.log(`PDF upload failed, keeping original data for retry:`, answer.pdfFile);
                        }
                    }
                }
                // Handle large text content (over 10KB) - store in Firebase Storage
                if (answer && typeof answer === 'object' && answer.text && typeof answer.text === 'string' && answer.text.length > 10000) {
                    const textBlob = new Blob([answer.text], { type: 'text/plain' });
                    const storageRef = firebase.storage().ref(`submissions/${examCode}/${sanitizedEmail}/${questionId}/answer.txt`);
                    const uploadTask = await storageRef.put(textBlob);
                    const downloadURL = await uploadTask.ref.getDownloadURL();
                    answer.text = {
                        type: 'storage_reference',
                        url: downloadURL,
                        size: answer.text.length
                    };
                }
                // If answer is a File (other file upload type)
                if (answer instanceof File) {
                    const storageRef = firebase.storage().ref(`submissions/${examCode}/${sanitizedEmail}/${questionId}/${answer.name}`);
                    const uploadTask = await storageRef.put(answer);
                    const downloadURL = await uploadTask.ref.getDownloadURL();
                    finalAnswers[questionId] = downloadURL;
                }
                // If answer is a long string (not rich text)
                if (typeof answer === 'string' && answer.length > 10000) {
                    const textBlob = new Blob([answer], { type: 'text/plain' });
                    const storageRef = firebase.storage().ref(`submissions/${examCode}/${sanitizedEmail}/${questionId}/answer.txt`);
                    const uploadTask = await storageRef.put(textBlob);
                    const downloadURL = await uploadTask.ref.getDownloadURL();
                    finalAnswers[questionId] = {
                        type: 'storage_reference',
                        url: downloadURL,
                        size: answer.length
                    };
                }
            }
            
            // Retry failed PDF uploads
            const failedPdfUploads = [];
            for (const questionId in finalAnswers) {
                const answer = finalAnswers[questionId];
                if (answer && typeof answer === 'object' && answer.pdfFile && answer.pdfFile.uploadFailed) {
                    failedPdfUploads.push({ questionId, answer });
                }
            }
            
            if (failedPdfUploads.length > 0) {
                console.log(`Retrying ${failedPdfUploads.length} failed PDF uploads...`);
                for (const { questionId, answer } of failedPdfUploads) {
                    try {
                        console.log(`Retrying PDF upload for question ${questionId}...`);
                        
                        // Generate new unique filename for retry
                        const timestamp = Date.now();
                        const randomId = Math.random().toString(36).substring(2, 15);
                        const originalFileName = answer.pdfFile.fileName;
                        const fileExtension = originalFileName.split('.').pop();
                        const uniqueFileName = `pdf_${timestamp}_${randomId}.${fileExtension}`;
                        
                        // Upload PDF to Firebase Storage
                        const storageRef = firebase.storage().ref(`submissions/${examCode}/${questionId}/${uniqueFileName}`);
                        const uploadTask = await storageRef.put(answer.pdfFile.file);
                        
                        // Update with successful upload
                        const pdfPath = `${examCode}/${questionId}/${uniqueFileName}`;
                        answer.pdfFile = {
                            fileName: uniqueFileName,
                            originalFileName: originalFileName,
                            pdfPath: pdfPath,
                            uploadTime: new Date().toISOString(),
                            size: answer.pdfFile.file.size,
                            uploaded: true
                        };
                        
                        // Extract PDF text and add to existing object
                        if (answer.pdfFile.file) {
                            try {
                                const PdfTextExtractionService = (await import('../services/PdfTextExtractionService')).default;
                                const extractionResult = await PdfTextExtractionService.extractText(answer.pdfFile.file);
                                if (extractionResult.text && extractionResult.text.trim()) {
                                    answer.pdfFile.extractedText = extractionResult.text;
                                }
                            } catch (extractionError) {
                                console.error(`PDF text extraction failed (retry):`, extractionError);
                                // Continue even if extraction fails - PDF is still uploaded
                            }
                        }
                        
                        console.log(`PDF retry successful for question ${questionId}:`, answer.pdfFile);
                    } catch (retryError) {
                        console.error(`PDF retry failed for question ${questionId}:`, retryError);
                        // Keep the failed state for debugging
                        answer.pdfFile.retryFailed = true;
                        answer.pdfFile.retryError = retryError.message;
                    }
                }
            }
            // --- END NEW ---

            // Get cheat counts from session storage
            const cheatCounts = {
                alt: sessionStorage.getItem("countalt") || "0",
                ctrl: sessionStorage.getItem("countctrl") || "0",
                tab: sessionStorage.getItem("count_tabchange") || "0",
                face: sessionStorage.getItem("count_facedetect") || "0",
                fullscreen: sessionStorage.getItem("count_fullscreen") || "0",
                copyPaste: sessionStorage.getItem("count_copypaste") || "0",
                webcamOff: sessionStorage.getItem("count_webcamoff") || "0",
                contextMenu: sessionStorage.getItem("count_contextmenu") || "0",
                noPerson: sessionStorage.getItem("count_noPerson") || "0",
                multiplePersons: sessionStorage.getItem("count_multiplePersons") || "0",
                suspiciousObjects: sessionStorage.getItem("count_suspiciousObjects") || "0",
                sidePanel: sessionStorage.getItem("count_sidepanel") || "0",
                focusLoss: sessionStorage.getItem("count_focusloss") || "0"
            };

            // Calculate total cheat count (only main violations: copy/paste, tab switch, webcam)
            const totalCheatCount = parseInt(cheatCounts.copyPaste) + parseInt(cheatCounts.tab) + parseInt(cheatCounts.webcamOff);

            // Step 4: Process video transcriptions (AFTER all uploads are complete, same pattern as PDF extraction)
            // CRITICAL: Videos must be uploaded to Firebase Storage first (just like PDFs)
            // Only then can we extract transcripts for AI evaluation
            let videoAnalysisResults = {};
            if (hasVideoQuestions) {
                const videoProcessingStep = getStepInfo('Processing video transcriptions...');
                setSubmissionProgress(prev => ({
                    ...prev,
                    currentStep: videoProcessingStep.stepIndex,
                    progress: videoProcessingStep.progress
                }));

                // Process video questions for transcription (same pattern as PDF text extraction)
                // Videos are already uploaded at this point, so we can safely extract transcripts
                const videoAnalysisPromises = [];
                exam.sections.forEach((section, sIndex) => {
                    section.questions.forEach((question, qIndex) => {
                        if (question.type === 'video') {
                            const answer = finalAnswers[question.id];
                            
                            // CRITICAL: Only proceed if we have a videoPath (Firebase Storage path)
                            // At this point, videos should already be uploaded, so videoPath must exist
                            if (!answer || !answer.videoPath) {
                                return;
                            }
                            
                            // Extract transcript from video (same pattern as PDF text extraction)
                            const transcriptionPromise = VideoAnalysisService.transcribeVideo(answer.videoPath)
                                .then(transcriptionResult => {
                                    if (transcriptionResult.success && transcriptionResult.transcript) {
                                        // Store transcript in answer object (same as PDF extractedText)
                                        const answerWithTranscript = {
                                            ...answer,
                                            transcript: transcriptionResult.transcript
                                        };
                                        finalAnswers[question.id] = answerWithTranscript;
                                        
                                        return {
                                            questionId: question.id,
                                            transcript: transcriptionResult.transcript,
                                            success: true
                                        };
                                    } else {
                                        return {
                                            questionId: question.id,
                                            transcript: '',
                                            success: false,
                                            error: transcriptionResult.error || 'Transcription failed'
                                        };
                                    }
                                })
                                .catch(error => {
                                    console.error('Video transcription error:', error);
                                    return {
                                        questionId: question.id,
                                        transcript: '',
                                        success: false,
                                        error: error.message || 'Transcription error'
                                    };
                                });
                            
                            videoAnalysisPromises.push(transcriptionPromise);
                        }
                    });
                });

                // Wait for all video transcriptions to complete (same pattern as PDF extraction)
                if (videoAnalysisPromises.length > 0) {
                    try {
                        await Promise.all(videoAnalysisPromises);
                    } catch (error) {
                        console.error('Error processing video transcriptions:', error);
                    }
                }
            }

            // Step 5: Saving submission data
            const savingStep = getStepInfo('Saving submission data...');
            setSubmissionProgress(prev => ({
                ...prev,
                currentStep: savingStep.stepIndex,
                progress: savingStep.progress
            }));



            // NOW create submission data with video analysis results
            const submissionData = {
                examCode,
                name: userName,
                email: userEmail,
                submittedAt: firebase.database.ServerValue.TIMESTAMP,
                answers: cleanFirebaseData(finalAnswers), // Clean answers to remove NaN values
                provisionalScore: cleanFirebaseData(provisionalScore), // Clean provisional score
                isDynamic: true,
                ...cheatCounts,
                totalCheatCount
            };

            // Note: Video transcripts are now stored directly in answer objects (same as PDF extractedText)
            // AI evaluation will extract transcripts from answer objects during evaluation

            // Estimate payload size (rough calculation)
            const payloadSize = JSON.stringify(submissionData).length;

            if (payloadSize > 900000) { // If over 900KB, split the data
                
                // Store answers separately
                const answersRef = firebase.database().ref(`Exam_results/${examCode}/${sanitizedEmail}/answers`);
                await answersRef.set(cleanFirebaseData(finalAnswers)); // Clean answers before storing
                
                // Store metadata separately
                const metadata = {
                    examCode,
                    email: userEmail,
                    submittedAt: firebase.database.ServerValue.TIMESTAMP,
                    isDynamic: true,
                    hasSeparateAnswers: true
                };
                await submissionRef.set(cleanFirebaseData(metadata)); // Clean metadata before storing
            } else {
                // Normal submission for smaller payloads
                await submissionRef.set(cleanFirebaseData(submissionData)); // Clean submission data before storing
            }
            
            // Note: Extracted PDF text will be added to answers during AI evaluation step below

            // Step 6: Evaluating answers with AI
            const aiEvaluationStep = getStepInfo('Evaluating answers with AI...');
            setSubmissionProgress(prev => ({
                ...prev,
                currentStep: aiEvaluationStep.stepIndex,
                progress: aiEvaluationStep.progress
            }));
            
            // Automatically trigger AI evaluation for the entire submission
            try {
                const evaluationResult = await AIEvaluationService.evaluateSubmission(exam, submissionData);
                
                // Clean the evaluation result to remove undefined values
                const cleanEvaluationResult = cleanFirebaseData(evaluationResult);
                
                // Save the complete evaluation result to the submission
                // Note: PDF extracted text is logged to file, not saved to database to save costs
                await submissionRef.update({
                    aiEvaluation: cleanEvaluationResult
                });
            } catch (aiError) {
                console.error('AI evaluation failed:', aiError);
                // Continue with submission even if AI evaluation fails
                await submissionRef.update({
                    aiEvaluation: {
                        error: aiError.message,
                        status: 'failed'
                    }
                });
            }

            // Step 7: Finalizing results
            const finalizingStep = getStepInfo('Finalizing results...');
            setSubmissionProgress(prev => ({
                ...prev,
                currentStep: finalizingStep.stepIndex,
                progress: finalizingStep.progress
            }));

            // Resume security alerts
            if (securityRef.current) {
                securityRef.current.resumeSecurityAlerts();
            }

            // Show success and redirect
            setSubmissionProgress(prev => ({
                ...prev,
                status: 'success'
            }));

            setTimeout(() => {
                setSubmissionProgress(prev => ({ ...prev, open: false }));
                history.push('/thankyou');
            }, 2000);

        } catch (error) {
            // Resume security alerts on error
            if (securityRef.current) {
                securityRef.current.resumeSecurityAlerts();
            }

            // Create detailed error message
            let detailedErrorMessage = 'There was an issue submitting your assessment. ';
            
            if (error.code === 'PERMISSION_DENIED') {
                detailedErrorMessage += 'Permission denied. Please check your authentication.';
            } else if (error.code === 'QUOTA_EXCEEDED') {
                detailedErrorMessage += 'Database quota exceeded. Please contact support.';
            } else if (error.message) {
                detailedErrorMessage += `Error: ${error.message}`;
            } else {
                detailedErrorMessage += 'Please try again.';
            }

            setSubmissionProgress(prev => ({
                ...prev,
                status: 'error',
                errorMessage: detailedErrorMessage
            }));

            setTimeout(() => {
                setSubmissionProgress(prev => ({ ...prev, open: false }));
            }, 5000); // 5 seconds
        } finally {
            setIsSubmitting(false);
        }
    };

    // Monitor timer state and auto-submit when timer reaches zero
    useEffect(() => {
        // Only auto-submit if timer was actually running and reached zero
        // Don't auto-submit on initial load when timer hasn't started yet
        if (minutes <= 0 && seconds <= 0 && !isSubmitting && !loading && exam && examReady && timerRef.current && timerStarted && mountedRef.current && !loading) {
            clearInterval(timerRef.current);
            sessionStorage.setItem("exam_timer", 0);
            sessionStorage.setItem("exam_sec", 0);
            autoSubmitExam();
        }
    }, [minutes, seconds, isSubmitting, loading, exam, examReady, timerStarted]);

    // Sync exam state to global storage for getDisplayUrl function
    useEffect(() => {
        if (exam) {
            window.currentExam = exam;
            window.currentExamState = exam;
        }
    }, [exam]);

    // Preload images for adjacent sections to improve performance
    const preloadSectionImages = useCallback((sectionIndex) => {
        if (!exam || !exam.sections) return;
        
        const sectionsToPreload = [sectionIndex];
        if (sectionIndex > 0) sectionsToPreload.push(sectionIndex - 1);
        if (sectionIndex < exam.sections.length - 1) sectionsToPreload.push(sectionIndex + 1);
        
        sectionsToPreload.forEach(sectionIdx => {
            const section = exam.sections[sectionIdx];
            if (section && section.questions) {
                section.questions.forEach(question => {
                    if (question.imageUrl && !question.imageUrl.includes('firebase')) {
                        // Preload short URL images
                        const img = new Image();
                        img.src = `image-questions/${question.id}/${question.imageUrl}`;
                    }
                });
            }
        });
    }, [exam]);

    // Sync timer values to ref whenever they change
    useEffect(() => {
        timerValuesRef.current = { minutes, seconds };
    }, [minutes, seconds]);

    // Monitor examReady state and start timer when ready
    useEffect(() => {
        if (examReady && (minutes > 0 || seconds > 0) && !timerRef.current && !timerStarted && mountedRef.current) {
            // Double-check: ensure no interval already exists (defensive programming)
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
            
            // Initialize ref with current values before starting timer
            timerValuesRef.current = { minutes, seconds };
            
            // Track if this is the first tick to prevent immediate decrement
            let tickCount = 0;
            let isProcessing = false; // Prevent concurrent executions
            
            const timerCallback = () => {
                // Check if component is still mounted before updating state
                if (!mountedRef.current) {
                    return;
                }
                
                // Prevent concurrent execution using global lock
                if (timerLockRef.current || isProcessing) {
                    return;
                }
                
                // Set both locks
                timerLockRef.current = true;
                isProcessing = true;
                tickCount++;
                
                // Get current values from ref (always up-to-date)
                const currentMinutes = timerValuesRef.current.minutes;
                const currentSeconds = timerValuesRef.current.seconds;
                
                // On first tick, if seconds is 0, skip decrementing (wait for full second to pass)
                // This prevents immediate jump from 10:00 to 9:59
                if (tickCount === 1 && currentSeconds === 0 && currentMinutes > 0) {
                    timerLockRef.current = false;
                    isProcessing = false;
                    return; // Don't decrement on first tick, wait for second tick
                }
                
                // Safety check: ensure we have valid timer values
                if (currentMinutes < 0 || currentSeconds < 0) {
                    timerLockRef.current = false;
                    isProcessing = false;
                    return;
                }
                
                // If both minutes and seconds are 0, stop the timer
                if (currentMinutes <= 0 && currentSeconds <= 0) {
                    if (timerRef.current) {
                        clearInterval(timerRef.current);
                        timerRef.current = null;
                    }
                    sessionStorage.setItem("exam_timer", "0");
                    sessionStorage.setItem("exam_sec", "0");
                    // Only auto-submit if timer was actually running
                    if (timerStarted && mountedRef.current) {
                        autoSubmitExam();
                    }
                    return;
                }
                
                // Use functional state updates - update seconds first, then minutes if needed
                // This ensures atomic updates and prevents double-decrement bugs
                setSeconds((prevSeconds) => {
                    // CRITICAL: Check ref value first - if it's already been updated, don't process again
                    const refMinutes = timerValuesRef.current.minutes;
                    const refSeconds = timerValuesRef.current.seconds;
                    
                    // If we're trying to process when seconds is 0 but ref already shows 59,
                    // it means we've already processed this minute rollover - skip to prevent double decrement
                    if (prevSeconds === 0 && refSeconds === 59) {
                        return refSeconds; // Return the ref value, don't process again
                    }
                    
                    const prevMinutes = timerValuesRef.current.minutes;
                    
                    // If both minutes and seconds are 0, stop the timer
                    if (prevMinutes <= 0 && prevSeconds <= 0) {
                        if (timerRef.current) {
                            clearInterval(timerRef.current);
                            timerRef.current = null;
                        }
                        sessionStorage.setItem("exam_timer", "0");
                        sessionStorage.setItem("exam_sec", "0");
                        if (timerStarted && mountedRef.current) {
                            autoSubmitExam();
                        }
                        timerValuesRef.current = { minutes: 0, seconds: 0 };
                        return 0;
                    }
                    
                    if (prevSeconds > 0) {
                        // Just decrement seconds, keep minutes same
                        const newSeconds = Math.max(0, prevSeconds - 1);
                        timerValuesRef.current.seconds = newSeconds;
                        sessionStorage.setItem("exam_sec", newSeconds.toString());
                        // Unlock after update
                        setTimeout(() => {
                            timerLockRef.current = false;
                            isProcessing = false;
                        }, 0);
                        return newSeconds;
                    } else if (prevMinutes > 0) {
                        // Seconds is 0 but minutes > 0, decrement minutes and set seconds to 59
                        // Use ref as single source of truth to prevent double decrement
                        const currentRefMinutes = timerValuesRef.current.minutes;
                        
                        // CRITICAL: Check if ref was already decremented (prevent double decrement)
                        if (currentRefMinutes < prevMinutes) {
                            // Ref was already updated, use it and don't decrement again
                            timerValuesRef.current.seconds = 59;
                            sessionStorage.setItem("exam_timer", currentRefMinutes.toString());
                            sessionStorage.setItem("exam_sec", "59");
                            setMinutes(currentRefMinutes);
                            // Unlock immediately
                            timerLockRef.current = false;
                            isProcessing = false;
                            return 59;
                        }
                        
                        // Safety check: ensure ref matches what we expect
                        if (currentRefMinutes !== prevMinutes) {
                            timerValuesRef.current.seconds = 59;
                            sessionStorage.setItem("exam_timer", currentRefMinutes.toString());
                            sessionStorage.setItem("exam_sec", "59");
                            setMinutes(currentRefMinutes);
                            timerLockRef.current = false;
                            isProcessing = false;
                            return 59;
                        }
                        
                        // Calculate new minutes value
                        const newMinutes = Math.max(0, prevMinutes - 1);
                        
                        // Update ref IMMEDIATELY as single source of truth (BEFORE any state updates)
                        timerValuesRef.current.minutes = newMinutes;
                        timerValuesRef.current.seconds = 59;
                        
                        // Write to sessionStorage immediately (ref is source of truth)
                        sessionStorage.setItem("exam_timer", newMinutes.toString());
                        sessionStorage.setItem("exam_sec", "59");
                        
                        // Update state from ref (ref is source of truth)
                        setMinutes(newMinutes);
                        
                        // Unlock immediately after updating state (synchronous unlock)
                        timerLockRef.current = false;
                        isProcessing = false;
                        
                        return 59;
                    } else {
                        // Both minutes and seconds are 0 - timer expired
                        if (timerRef.current) {
                            clearInterval(timerRef.current);
                            timerRef.current = null;
                        }
                        sessionStorage.setItem("exam_timer", "0");
                        sessionStorage.setItem("exam_sec", "0");
                        if (timerStarted && mountedRef.current) {
                            autoSubmitExam();
                        }
                        timerValuesRef.current = { minutes: 0, seconds: 0 };
                        // Unlock before returning
                        setTimeout(() => {
                            timerLockRef.current = false;
                            isProcessing = false;
                        }, 0);
                        return 0;
                    }
                });
            };
            
            timerRef.current = setInterval(timerCallback, 1000);
            setTimerStarted(true); // Mark that timer has actually started
        }
    }, [examReady, timerStarted]); // Removed minutes and seconds from dependencies to prevent recreating interval

    // Stop timer when it reaches zero
    useEffect(() => {
        if (minutes <= 0 && seconds <= 0 && timerRef.current && timerStarted) {
            clearInterval(timerRef.current);
            timerRef.current = null;
            setTimerStarted(false);
        }
    }, [minutes, seconds, timerStarted]);

    // Keep sessionStorage synchronized with React state
    // NOTE: We only sync when timer is running and values are valid
    // The timer callback itself writes to sessionStorage, so this is just a backup
    useEffect(() => {
        // Only sync if timer is actually running and we have valid values
        // Don't sync during initial load or when timer hasn't started yet
        if (examReady && timerStarted && mountedRef.current && timerRef.current) {
            // Use ref values instead of state to avoid stale values
            const refMinutes = timerValuesRef.current.minutes;
            const refSeconds = timerValuesRef.current.seconds;
            
            // Only sync if ref values are different from what's in sessionStorage
            const storedMinutes = parseInt(sessionStorage.getItem("exam_timer") || "0");
            const storedSeconds = parseInt(sessionStorage.getItem("exam_sec") || "0");
            
            // Only update if values are actually different (prevent unnecessary writes)
            if (refMinutes !== storedMinutes || refSeconds !== storedSeconds) {
                const safeMinutes = Math.max(0, refMinutes);
                const safeSeconds = Math.max(0, refSeconds);
                sessionStorage.setItem("exam_timer", safeMinutes.toString());
                sessionStorage.setItem("exam_sec", safeSeconds.toString());
            }
        }
        
        // Cleanup function to prevent memory leaks
        return () => {
            // Component is unmounting, don't update sessionStorage
        };
    }, [minutes, seconds, examReady, timerStarted]);

    useEffect(() => {
        if (prevSectionRef.current !== currentSection) {
            setStepperAnim('stepper-animate');
            setTimeout(() => setStepperAnim(''), 500);
            prevSectionRef.current = currentSection;
            
            // Preload images for the new section and adjacent sections
            preloadSectionImages(currentSection);
        }
    }, [currentSection, preloadSectionImages]);

    if (loading) {
        return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
            <div style={{ textAlign: 'center' }}>
                <CircularProgress />
                <div style={{ marginTop: '20px', color: '#666' }}>Loading assessment...</div>
            </div>
        </div>;
    }

    if (!exam) {
        return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
            <Typography variant="h5" color="error">Assessment not found.</Typography>
        </div>;
    }

    return (
        <div className="exam-screen-root">
            {/* Submission Progress Modal */}
            <SubmissionProgressModal
                open={submissionProgress.open}
                onClose={() => setSubmissionProgress(prev => ({ ...prev, open: false }))}
                steps={terminationNotice ? [
                    terminationNotice,
                    'Saving the responses you have given so far...',
                    'Closing your assessment...'
                ] : [
                    'Reviewing your brilliant responses...',
                    'Collecting your amazing uploads...',
                    'Preparing your submission...',
                    'Securing everything safely...',
                    'Almost there...'
                ]}
                currentStep={submissionProgress.currentStep}
                progress={submissionProgress.progress}
                status={submissionProgress.status}
                errorMessage={submissionProgress.errorMessage}
            />
            {/* Left Panel */}
            <div className="left-panel">
                {!isLoadTesting && !showDetection && !isSubmitting && (
                    <div className="detect" style={{ padding: '20px', textAlign: 'center', color: '#666', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
                        <div>Security monitoring will start shortly...</div>
                    </div>
                )}
                {!isLoadTesting && isSubmitting && (
                    <div className="detect" style={{ padding: '20px', textAlign: 'center', color: '#666', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
                        <div>Submitting your assessment...</div>
                    </div>
                )}
                {!isLoadTesting && showDetection && !isSubmitting && (
                    <div className="detect">
                        <Suspense fallback={<div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>Initializing security monitoring...</div>}>
                            <Detection handleDetectionAlert={(message, type, callback) => {
                                // Map detection types to cheat types
                                let cheatType = type;
                                if (message.includes("No person detected") || message.includes("camera is obstructed")) {
                                    cheatType = 'noPerson';
                                } else if (message.includes("Multiple persons detected")) {
                                    cheatType = 'multiplePersons';
                                } else if (message.includes("cell phone") || message.includes("book") || message.includes("laptop") || message.includes("Suspicious object")) {
                                    cheatType = 'suspiciousObjects';
                                }
                                
                                // Check if webcam detection is enabled before showing alert
                                const featureKey = 'enableWebcamDetection';
                                const isEnabled = cheatDetectionSettings && CheatDetectionConfig.isFeatureEnabled(cheatDetectionSettings, featureKey);
                                
                                if (!isEnabled) {
                                    // Feature is disabled, don't show alert or record violation
                                    if (callback) callback();
                                    return;
                                }
                                
                                // Record the violation in the security system
                                let alertedBySecurity = false;
                                if (securityRef.current) {
                                    alertedBySecurity = securityRef.current.recordDetectionViolation(cheatType, message);
                                }

                                // When the violation was recorded the security handler already showed the
                                // "Warning X of Y" alert - a second dialog here would just stack on top of it
                                if (alertedBySecurity || examTerminatedRef.current) {
                                    if (callback) callback();
                                    return;
                                }

                                // Show alert to user using SweetAlert
                                swal({
                                    title: "Security Violation Detected",
                                    text: message,
                                    icon: type === 'error' ? 'error' : 'warning',
                                    button: "OK",
                                    closeOnClickOutside: false,
                                    closeOnEsc: false,
                                    dangerMode: type === 'error'
                                }).then(() => {
                                    if (callback) callback();
                                });
                            }} />
                        </Suspense>
                    </div>
                )}
                <div className="timer-box">
                    <div className="timer-label">Remaining Time</div>
                    <div className="timer-value">
                        {(() => {
                            const displayMinutes = Math.max(0, minutes);
                            const displaySeconds = Math.max(0, seconds);
                            return `${displayMinutes}:${displaySeconds < 10 ? `0${displaySeconds}` : displaySeconds}`;
                        })()}
                    </div>
                </div>
                <div className="user-info-box">
                    <div className="user-info">Name: <span className="user-info-accent">{sessionStorage.getItem("checkname")}</span></div>
                    <div className="user-info">Email: <span className="user-info-accent">{sessionStorage.getItem("checkemail")}</span></div>
                </div>
                <div className="instructions-box">
                    <div className="instructions-title">Assessment Protocol</div>
                    <ul className="instructions-list">
                        <li>✅ Maintain camera visibility - facial recognition monitoring is active</li>
                        <li>⏰ Automatic data preservation every 30 seconds - manual intervention not required</li>
                        <li>🚫 Prohibit browser tab switching or external device usage</li>
                        <li>📋 Text replication permitted for video response questions only</li>
                        <li>💾 Response data is automatically preserved - focus on assessment content</li>
                        <li>⚠️ Maintain browser session - page refresh will trigger automatic submission</li>
                        <li>📱 Remove mobile devices from workspace - unauthorized objects are monitored</li>
                    </ul>
                </div>

                

                
            </div>
            {/* Right Panel */}
            <div className="right-panel">
                <div className="right-panel-inner">
                    {/* Fixed Stepper */}
                    <nav className="stepper">
                      <ol className="stepper-steps">
                        {exam.sections.map((section, idx) => {
                          // Check if this section has incomplete questions
                          const hasIncompleteQuestions = section.questions.some(q => questionErrors[q.id]);
                          
                          return (
                            <li
                              className="stepper-step"
                              key={section.id}
                            >
                              <button
                                type="button"
                                className="stepper-step-button"
                                onClick={() => setCurrentSection(idx)}
                              >
                                <span
                                  className={`stepper-step-indicator ${
                                    currentSection === idx ? 'active' : 
                                    idx < currentSection ? 'completed' : ''
                                  } ${hasIncompleteQuestions ? 'incomplete' : ''}`}
                                >
                                  {idx < currentSection ? '✓' : idx + 1}
                                  {hasIncompleteQuestions && (
                                    <span className="incomplete-indicator" title="Incomplete questions">
                                      ⚠️
                                    </span>
                                  )}
                                </span>
                                <span
                                  className={`stepper-step-label ${
                                    currentSection === idx ? 'active' : 
                                    idx < currentSection ? 'completed' : ''
                                  } ${hasIncompleteQuestions ? 'incomplete' : ''}`}
                                >
                                  {section.title || `Section ${idx + 1}`}
                                  {hasIncompleteQuestions && (
                                    <span className="incomplete-count">
                                      ({section.questions.filter(q => questionErrors[q.id]).length} incomplete)
                                    </span>
                                  )}
                                </span>
                              </button>
                              {/* Connector line */}
                              {idx < exam.sections.length - 1 && (
                                <span
                                  className={`stepper-connector ${
                                    idx < currentSection ? 'completed' : ''
                                  }`}
                                />
                              )}
                            </li>
                          );
                        })}
                      </ol>
                    </nav>
                    <div className="progress-bar-header">
                        <span className="progress-bar-icon">{sectionIcons[currentSection % sectionIcons.length]}</span>
                        <span className="progress-bar-title">{`Section ${currentSection + 1} of ${totalSections}`}</span>
                        {/* <span className="progress-bar-section-title">{exam.sections[currentSection].title}</span> */}
                    </div>
                    <form onSubmit={e => { e.preventDefault(); handleSubmit(); }} className="exam-form">
                        <div className="section-form-content">
                            {/* Only render the current section */}
                            <div key={exam.sections[currentSection].id} className="section-card" style={{
                                boxShadow: `0 2px 12px ${sectionColors[currentSection % sectionColors.length]}22`, // 22 for ~13% opacity
                                borderLeft: `6px solid ${sectionColors[currentSection % sectionColors.length]}`
                            }}>
                                <div className="section-title" style={{display: 'flex', alignItems: 'center'}}>
                                    <span className="section-title-icon" style={{color: sectionColors[currentSection % sectionColors.length]}}>
                                        {sectionIcons[currentSection % sectionIcons.length]}
                                    </span>
                                    <span style={{fontSize: 22, fontWeight: 700, color: sectionColors[currentSection % sectionColors.length], letterSpacing: 0.5, marginLeft: 5}}>
                                        {exam.sections[currentSection].title}
                                    </span>
                                    {exam.sections[currentSection].weightage !== undefined && (
                                        <span style={{fontSize: 16, color: '#888', fontWeight: 500, marginLeft: 8}}>
                                            ({exam.sections[currentSection].weightage}% weightage)
                                        </span>
                                    )}
                                </div>
                                {exam.sections[currentSection].questions.map((question, qIndex) => {
                                    const sectionColor = sectionColors[currentSection % sectionColors.length];
                                    
                                    // Update security system with current question type
                                    if (securityRef.current) {
                                        securityRef.current.updateCurrentQuestionType(question.type);
                                    }
                                    
                                    return (
                                        <div key={question.id}>
                                            {['text', 'scenario-text'].includes(question.type) ? (
                                                // For text and scenario-text questions, render without question-card wrapper
                                                <div>
                                                    <div className="question-title" style={{color: '#1976d2', fontWeight: 700, fontSize: 17, marginBottom: 16}}>{`${qIndex + 1}. ${question.text}`}</div>
                                                    <EnhancedQuestionRenderer
                                                        question={question}
                                                        answer={answers[question.id]}
                                                        onAnswerChange={handleAnswerChange}
                                                        examCode={examCode}
                                                        userId={user?.uid || 'anonymous'}
                                                        disabled={isSubmitting}
                                                    />
                                                    {questionErrors[question.id] && (
                                                        <div style={{ color: 'red', fontSize: 14, marginTop: 4 }}>{questionErrors[question.id]}</div>
                                                    )}
                                                </div>
                                            ) : (
                                                // For other question types, use the question-card wrapper
                                                <div className="question-card">
                                            <div className="question-title" style={{color: '#1976d2', fontWeight: 700, fontSize: 17}}>{`${qIndex + 1}. ${question.text}`}</div>
                                                    {/* Question Image Display */}
                                                    {question.imageUrl && (
                                                        <div style={{ 
                                                            marginTop: 8, 
                                                            marginBottom: 24, 
                                                            padding: 24, 
                                                            background: '#f8f9fa', 
                                                            border: '1px solid #e9ecef',
                                                            borderRadius: 8 
                                                        }}>
                                                            <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 8, color: '#1976d2', fontSize: 14 }}>
                                                                📷 Question Image
                                                            </Typography>
                                                            <AuthenticatedImage 
                                                                imageUrl={question.imageUrl}
                                                                questionId={question.id}
                                                                alt="Question" 
                                                                style={{ 
                                                                    maxHeight: question.type === 'video' ? 800 : 650, // 30% larger images for better visibility
                                                                    width: '100%'
                                                                }} 
                                                            />
                                                        </div>
                                                    )}
                                                    {/* Render different question types */}
                                            {['mcq-single', 'mcq-multi'].includes(question.type) ? (
                                                <RadioGroup 
                                                    value={question.type === 'mcq-multi' ? (Array.isArray(answers[question.id]) ? answers[question.id][0] || '' : answers[question.id] || '') : (answers[question.id] || '')} 
                                                    onChange={e => {
                                                        if (question.type === 'mcq-multi') {
                                                            // For multi-choice, store as array with single selection
                                                            handleAnswerChange(question.id, [e.target.value]);
                                                        } else {
                                                            // For single-choice, store as string
                                                            handleAnswerChange(question.id, e.target.value);
                                                        }
                                                    }}
                                                >
                                                    {question.options.map(opt => (
                                                        <FormControlLabel
                                                            key={opt.id}
                                                            value={opt.id}
                                                            control={<Radio className="question-radio" style={{color: '#222'}} />}
                                                            label={<span className="question-option-label" style={{color: '#222', fontWeight: 700}}>{opt.text}</span>}
                                                            className="question-option-row"
                                                        />
                                                    ))}
                                                </RadioGroup>
                                            ) : (
                                                <div>
                                                    {/* Display instructions for video questions */}
                                                    {question.type === 'video' && question.instructions && (
                                                        <div style={{
                                                            marginTop: 16,
                                                            marginBottom: 20,
                                                            padding: 16,
                                                            background: '#e3f2fd',
                                                            border: '1px solid #90caf9',
                                                            borderRadius: 8,
                                                            borderLeft: '4px solid #1976d2'
                                                        }}>
                                                            <Typography variant="subtitle2" style={{ 
                                                                fontWeight: 600, 
                                                                marginBottom: 8, 
                                                                color: '#1976d2',
                                                                fontSize: 15,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 8
                                                            }}>
                                                                📋 Instructions
                                                            </Typography>
                                                            <Typography variant="body2" style={{ 
                                                                color: '#333', 
                                                                whiteSpace: 'pre-line',
                                                                lineHeight: 1.6,
                                                                fontSize: 10,
                                                                fontWeight: 'bold'
                                                            }}>
                                                                {question.instructions}
                                                            </Typography>
                                                        </div>
                                                    )}
                                                    <QuestionRenderer
                                                        ref={question.type === 'video' ? (ref) => {
                                                            if (ref) {
                                                                videoRecorderRefs.current[question.id] = ref;
                                                            }
                                                        } : null}
                                                        question={question}
                                                        questionNumber={qIndex + 1}
                                                        onAnswerChange={handleAnswerChange}
                                                        answer={answers[question.id]}
                                                        compact={true}
                                                        examCode={examCode}
                                                        userId={user?.uid || 'anonymous'}
                                                    />
                                                </div>
                                            )}
                                            {questionErrors[question.id] && (
                                                <div style={{ color: 'red', fontSize: 14, marginTop: 4 }}>{questionErrors[question.id]}</div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                        {/* Floating Navigation Buttons */}
                        <div className="fab-navigation-group">
                            {currentSection > 0 && (
                                <OverlayTrigger placement="left" overlay={<Tooltip>Previous Section</Tooltip>}>
                                    <Button
                                        variant="outline-secondary"
                                        size="lg"
                                        className="prev-btn"
                                        onClick={e => { e.preventDefault(); setCurrentSection(s => Math.max(0, s - 1)); }}
                                    >
                                        <ArrowLeft size={22} style={{ marginRight: 6 }} />
                                        <span className="fab-btn-text">Prev</span>
                                    </Button>
                                </OverlayTrigger>
                            )}
                            {currentSection < totalSections - 1 ? (
                                <OverlayTrigger placement="left" overlay={<Tooltip>Next Section</Tooltip>}>
                                    <Button
                                        variant="primary"
                                        size="lg"
                                        className="next-btn"
                                        onClick={e => { e.preventDefault(); setCurrentSection(s => Math.min(totalSections - 1, s + 1)); }}
                                    >
                                        <ArrowRight size={22} style={{ marginRight: 6 }} />
                                        <span className="fab-btn-text">Next</span>
                                    </Button>
                                </OverlayTrigger>
                            ) : (
                                <OverlayTrigger placement="left" overlay={<Tooltip>Submit Assessment</Tooltip>}>
                                    <Button
                                        variant="success"
                                        size="lg"
                                        className="submit-btn"
                                        type="submit"
                                        disabled={isSubmitting}
                                        onClick={handleSubmit}
                                    >
                                        <CheckCircle size={22} style={{ marginRight: 6 }} />
                                        <span className="fab-btn-text">Submit</span>
                                    </Button>
                                </OverlayTrigger>
                            )}
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default DynamicExamScreen; 